/**
 * Librería Javascript para funciones necesarias para Postgrados UAI
 * @copyright 2019-onwards Universidad Adolfo Ibáñez <www.uai.cl>
 * @author Jorge Villalón <jorge.villalon@uai.cl>
 **/
import React from "react"
import {
  FaBookReader,
  FaBriefcase,
  FaBullhorn,
  FaDollarSign,
  FaFileInvoiceDollar,
  FaHandHoldingUsd,
  FaUserGraduate,
  FaUserShield,
  FaUserTie,
} from "react-icons/fa"
import IconoCohorte from "./Utils/IconoCohorte"
import IconoContratos from "./Utils/IconoContratos"
import facultades from "./data/facultades.json"
import unidadesventa from "./data/unidadesventa.json"
import users from "./data/users.json"
import _vistas from "./Vistas"
import IconoPostulante from "./Utils/IconoPostulante"
import IconoReporte from "./Utils/IconoReporte"
import cloneDeep from "lodash/cloneDeep"
import localforage from "localforage"
import atributos from "./Atributos"
import log, { debug } from "loglevel"
import programasData from "./data/programas.json"
import encuestas from "./data/encuestas.json"
import escalas from "./data/escalas.json"
import UAIStats from "./Stats/UAIStats"
import diccionarioEncuestas from "./data/diccionarioencuestas.json"
import {
  MdCalendarToday,
  MdDescription
} from "react-icons/md"
import columns from "./ReporteMatriculas/ColumnasReporte"
import { Buffer } from "buffer"
import IconoCalidad from "./Utils/IconoCalidad"
import IconoConfiguracion from "./Utils/IconoConfiguracion"

/**
 * Clase genérica UAI para recopilar funciones comunes de conexión a sistemas UAI
 * y configuración general de la aplicación
 */
class UAI {
  /** Largo mínimo para buscador general */
  static largoMinimoBuscadorGeneral = 3
  /** Para paginación en listados */
  static resultadosPorPagina = 50
  /** Activar el tour por la aplicación */
  static ayudaActivada = false
  /** Tipos de ordenamiento de los tabs Facultad / Unidades Venta */
  static tabTypes = {
    FACULTADES_UNIDADESVENTA: 1,
    UNIDADESVENTA_FACULTADES: 2,
  }
  /** Administrador del sistema */
  static administrador = "Soporte Postula soportepostula@uai.cl"
  /** Objeto que consolida la configuración de la aplicación */
  static configuracion = null
  /** Quienes tienen permisos para impersonar a otros */
  static permisosImpersonar = {
    "jorge.villalon": { allowed: ["*"], forbidden: [] },
    "alejandro.donoso": { allowed: ["*"], forbidden: [] },
    "ernesto.jaramillo": { allowed: ["*"], forbidden: [] },
    "valentina.lorca": { allowed: ["*"], forbidden: [] },
    "joaquin.leal": { allowed: ["*"], forbidden: [] },
    "natalia.martinez.v": { allowed: ["*"], forbidden: [] },
    "daniela.mendez": { allowed: ["*"], forbidden: [] },
    "angela.martinez": { allowed: ["*"], forbidden: [] },
    "fernando.rodriguezv": {
      allowed: ["*"],
      forbidden: ["jorge.villalon", "ernesto.jaramillo", "rmachero"],
    },
    "salvador.rodriguez": {
      allowed: ["*"],
      forbidden: ["jorge.villalon", "ernesto.jaramillo", "rmachero"],
    },
    "sofia.martinez.b": {
      allowed: ["*"],
      forbidden: ["jorge.villalon", "ernesto.jaramillo", "rmachero"],
    },
  }
  /** Atributos por los cuales se pueden buscar cohortes */
  static atributosBusquedaCohortes = ["nombre", "sigla", "nombrecorto"]
  /** Variable con el URL de base de la aplicación incluyendo host, para la creación de URLs fijos */
  static wwwroot = function () {
    switch (process.env.REACT_APP_ENVIRONMENT) {
      case "dev":
        return "http://localhost:3000"
      case "staging":
                return "https://purple-grass-01d807e0f.4.azurestaticapps.net"
      case "prod":
        return "https://panelonline.uai.cl"
    }
  }
  /** Variable con el URL de base de la aplicación de backend */
  static backend = function () {
    switch (process.env.REACT_APP_ENVIRONMENT) {
      case "dev":
        return "http://localhost:3001"
      case "staging":
        return "https://onlinedev.uai.cl:8081"
      case "prod":
        return "https://onlinedev.uai.cl:8081"
    }
  }
  /** Base del URL sin host */
  static basename = function () {
    switch (process.env.REACT_APP_ENVIRONMENT) {
      case "dev":
      case "staging":
        return ""
      case "prod":
        return ""
    }
  }
  /** URL del servicio de Postula para conseguir información */
  static basePostula = function () {
    switch (process.env.REACT_APP_ENVIRONMENT) {
      case "dev":
      case "staging":
        return "https://democentral1.uai.cl/InternalServices_PanelOnline/api/"
      case "prod":
        return "https://finanzas.uai.cl/InternalServices/api/"
    }
  }
  /** Vistas por defecto por rol */
  static vistasPorDefecto = {
    Administrador: "info-general",
    "Director de Programa": "entrevistas",
    Marketing: "marketing",
    "Director de Ventas": "reportematriculas",
    "Director Ejecutivo Postgrados": "info-general",
    "Coordinadora Admision": "reportematriculas",
    "Coordinadora Académica": "miscohortes",
    Finanzas: "finanzas",
    Contabilidad: "finanzas",
    "Dirección de Docencia": "info-academica",
    OTEC: "info-general",
  }
  /** Ícono de cada rol. Para mostrar la matriz de permisos. */
  static iconosRoles = {
    Administrador: <FaUserTie />,
    "Director de Programa": <FaUserShield />,
    Marketing: <FaBullhorn />,
    "Director de Ventas": <FaDollarSign />,
    "Director Ejecutivo Postgrados": <FaUserTie />,
    "Coordinador de Admisión": <FaBookReader />,
    Finanzas: <FaHandHoldingUsd />,
    Contabilidad: <FaFileInvoiceDollar />,
    "Dirección de Docencia": <FaUserGraduate />,
    OTEC: <FaBriefcase />,
  }
  /** Objeto con las vistas que tiene la aplicación */
  static vistas = _vistas
  /** Objeto con los atributos disponibles en el sistema */
  static atributos = atributos
  /** Listado de atributos con servicio para buscar valores posibles */
  static atributosTypeAhead = ["profesores_nucleo"]
  /** Nombres de los grados */
  static nombresGrados = {
    1: "Diplomado",
    2: "Magíster",
    3: "Curso",
    4: "MBA",
    5: "Doctorado",
    7: "Seminario",
    8: "Charla",
    9: "Beca",
    10: "Certificado",
  }

  static meses = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ]
  /** Orden para que aparezcan de los grados */
  static ordenGrados = {
    1: "30", // "Diplomado",
    2: "20", // "Magíster",
    3: "40", // "Curso",
    4: "10", // "MBA",
    5: "00", // "Doctorado",
    7: "50", // "Seminario",
    8: "60", // "Charla",
  }

  /** Listado de atributos mínimos a cargar desde Postula para los cohortes */
  static atributosMinimos = [
    "nombre",
    "sigla",
    "dictado",
    "unidadventa",
    "fecha_inicio",
    "gradoid",
    "programaid_anterior",
    "GradoId",
    "FacultadId",
    "PasoActualId",
    "MaestroId",
    "usuario_coordinador_admision",
    "NombreCoordinadorAdmision",
    "coordinador_academico",
    "NombreCoordinadorAcademico",
    "facultades_secundarias",
    "programa_padre_id",
  ]

  static columnasReporteMatriculasPorDefecto = [
    "fechainicio",
    "visitantes",
    "interesados",
    "postulantes",
    "porentrevistar",
    "aceptados",
    "matriculados",
    "equivalentesperiodo",
    "metaequivalentes",
    "ingresos",
  ]

  static tipoProductos = [
    { key: "MBA", value: "7" },
    { value: "8", key: "Beca" },
    { value: "9", key: "Certificado" },
    { value: "6", key: "Charla" },
    { value: "5", key: "Seminario" },
    { value: "1", key: "Curso" },
    { value: "2", key: "Diplomado" },
    { value: "3", key: "Magíster" },
    {
      value: "4",
      key: "Doctorado",
    },
  ]

  static obtieneCohorteDesdeServicio = function (cohorte) {
    let output = cohorte.CohortesSalida
    if (!output) {
      log.error(cohorte)
      throw new Error("Cohorte inválido")
    }
    output.FacultadId = cohorte.FacultadId
    output.MaestroId = cohorte.MaestroId
    output.PasoActualId = cohorte.PasoActualId
    output.CohorteId = cohorte.CohorteId
    output.Rol = cohorte.Rol
    return output
  }

  static obtieneTipoProducto = function (cohorte) {
    if (!cohorte) return null
    let nombreGrado = UAI.nombresGrados[cohorte.gradoid]
    if (!nombreGrado) {
      return null
    }
    let tipoProducto = UAI.tipoProductos.find((t) => t.key === nombreGrado)
    if (!tipoProducto) {
      return null
    }
    return tipoProducto.value
  }

  static pasos = [
    {
      id: 1,
      title: "General",
    },
    {
      id: 2,
      title: "Académica",
    },
    {
      id: 3,
      title: "Admisión",
    },
    {
      id: 4,
      title: "Marketing & Finanzas",
    },
    {
      id: 6,
      title: "Publicado",
    },
  ]

  /**
   * Verifica si un cohorte debiera aparecer en el dashboard de coordinación de programa
   * @param {Cohorte} c cohorte a verificar
   * @param {Integer} año período a revisar
   * @returns
   */
  static cohorteApareceDashboard = function (c, año) {
    let fechaInicio = new Date(Date.parse(c.fecha_inicio))
    if (isNaN(fechaInicio)) {
      return false
    }
    let añoInicio = fechaInicio.getFullYear()
    let fechaTermino = new Date(Date.parse(c.fecha_termino))
    let añoTermino = añoInicio
    if (c.gradoid === 2 || c.gradoid === 4 || c.gradoid === 5) {
      añoTermino += 2
    } else if (c.gradoid === 1) {
      añoTermino += 1
    }
    if (!isNaN(fechaTermino)) {
      fechaTermino.setDate(fechaTermino.getDate() + 60)
      añoTermino = fechaTermino.getFullYear()
    }
    let aparece =
      (c.esborrador && añoInicio >= año) ||
      (!c.esborrador && (añoInicio >= año || añoTermino === año))
    return aparece
  }

  /**
   * Etapas del ciclo de vida de un cohorte
   */
  static EtapasCicloVidaCohortes = {
    preparacion: {
      key: "preparacion",
      nombre: "Preparación",
      filtro: (c, año) => {
        return UAI.cohorteApareceDashboard(c, año) && c.esborrador
      },
      criticos: [
        "cohorteid_spa", // ID en SPA se consigue via Centro de Costo
        "calendario_detallado", // Catering, salas y profesores
        "profesores_nucleo",
        "bienvenida_automatica", // Email de bienvenida, personalizable por cohordinadora
        "cohorteid_omega",
        "cohorteid_moodle",
        "merchandising",
      ],
    },
    planificacion: {
      key: "planificacion",
      nombre: "Planificación",
      filtro: (c, año) => {
        let hoy = new Date()
        let fechaInicio = new Date(Date.parse(c.fecha_inicio))
        return (
          UAI.cohorteApareceDashboard(c, año) &&
          !c.esborrador &&
          fechaInicio > hoy
        )
      },
      criticos: [
        "fecha_inicio",
        "fecha_termino",
        "sede_id",
        "horario_clases",
        "director_programa",
        "cohorteid_omega",
        "coordinador_academico",
        "profesores_nucleo",
        "Modalidad",
        "formato",
        "calendario_detallado", // Fechas
      ],
    },
    ejecucion: {
      key: "ejecucion",
      nombre: "Ejecución",
      filtro: (c, año) => {
        let hoy = new Date()
        let fechaInicio = new Date(Date.parse(c.fecha_inicio))
        let fechaTermino = new Date(Date.parse(c.fecha_termino))
        return (
          UAI.cohorteApareceDashboard(c, año) &&
          !c.esborrador &&
          fechaInicio <= hoy &&
          (isNaN(fechaTermino) || hoy <= fechaTermino)
        )
      },
      criticos: [
        "actividades_spa",
        "avancehoras_spa",
        "oc_catering", // Órdenes de compra
        "situacion_financiera",
        "solicitudes_cambios_sala",
        "asistencia",
        "calificaciones",
        "encuesta_inicio_programa",
        "encuestas_docentes",
        "encuesta_general_satisfaccion",
        "encuesta_salida_programa",
        "material_webcursos",
        "ayudantias",
        "calendario_detallado",
        "resoluciones_omega",
      ],
    },
    cierre: {
      key: "cierre",
      nombre: "Cierre y Mejora Continua",
      filtro: (c, año) => {
        let hoy = new Date()
        let fechaTermino = new Date(Date.parse(c.fecha_termino))
        return (
          UAI.cohorteApareceDashboard(c, año) &&
          !c.esborrador &&
          hoy > fechaTermino
        )
      },
      criticos: [
        "actividad_cierre", // Cheese & Wine
        "graduacion",
        "electivos",
        "cierre_expedientes",
      ],
    },
  }

  /** Listado de encuestas, IDs, nombres */
  static encuestas = encuestas
  /** Escalas con nombres y parámetros */
  static escalas = escalas
  /** Diccionario de encuestas, sus preguntas e indicadores */
  static diccionarioEncuestas = diccionarioEncuestas

  /* Funciones utilizadas para indicadores */
  static existeValor = function (_value) {
    if (
      _value === undefined ||
      _value === null ||
      _value === "null" ||
      _value === "NULL"
    ) {
      return 0
    } else {
      return 1
    }
  }

  static parseFacultadesSecundarias = function (cohorte) {
    // [{"selected":true,"disabled":false,"text":"Facultad de Ingeniería y Ciencias","id":"3","title":"","_resultId":"select2-facultades_secundarias-result-t3xj-3","element":{}}]
    let jsonFacultades = cohorte["facultades_secundarias"]
    if (!jsonFacultades || jsonFacultades.length === 0) {
      return ""
    }
    let parsedFacultades = null
    try {
      parsedFacultades = JSON.parse(jsonFacultades)
    } catch (e) {
      return "Datos inválidos"
    }
    if (!Array.isArray(parsedFacultades)) {
      return "Datos inválidos"
    }
    return <ul>{parsedFacultades.map((facultad, idx) => <li key={facultad.key + "-" + idx}>{facultad.text}</li>)}</ul>
  }

  static obtieneTabTypeActual = function (usuario) {
    let storage = window.localStorage
    if (!storage.getItem("tabType")) {
      storage.setItem("tabType", usuario.tabType)
    }
    let tabType = parseInt(storage.getItem("tabType"))
    if (tabType > 0) {
      return tabType
    } else {
      throw new Error("Valor de Tab Type inválido")
    }
  }

  static obtieneDefaultsGroupby = function (usuario) {
    let groupby = {}
    Object.keys(usuario.facultades).forEach((kf) => {
      Object.keys(usuario.facultades[kf].unidadesventa).forEach((ku) => {
        groupby[kf + "-" + ku] = "grupo_reporte_matriculados"
      })
      groupby[kf + "-resumentodos"] = "usuario_coordinador_admision"
    })
    Object.keys(usuario.unidadesventa).forEach((ku) => {
      groupby["resumentodos-" + ku] = "usuario_coordinador_admision"
    })
    return groupby
  }

  static obtieneGroupbyActual = function (usuario) {
    let storage = window.localStorage
    let groupby = storage.getItem("groupby")
    if (!groupby || groupby === "undefined" || groupby === "null") {
      groupby = UAI.obtieneDefaultsGroupby(usuario)
      storage.setItem("groupby", JSON.stringify(groupby))
    }
    try {
      groupby = JSON.parse(groupby)
    } catch (e) {
      groupby = UAI.obtieneDefaultsGroupby(usuario)
      storage.setItem("groupby", JSON.stringify(groupby))
    }
    if (typeof groupby === "object") {
      return groupby
    } else {
      log.debug(groupby)
      throw new Error("Valor de groupby inválido")
    }
  }

  static obtieneDefaultsColumns = function (usuario, tipoColumnas) {
    let defaultColumns = {}
    if (tipoColumnas === "postulantes") {
      Object.keys(usuario.facultades).forEach((kf) => {
        Object.keys(usuario.facultades[kf].unidadesventa).forEach((ku) => {
          defaultColumns[kf + "-" + ku] =
            UAI.columnasReporteMatriculasPorDefecto
        })
        defaultColumns[kf + "-resumentodos"] =
          UAI.columnasReporteMatriculasPorDefecto
        defaultColumns[kf + "-resumen"] =
          UAI.columnasReporteMatriculasPorDefecto
      })
      Object.keys(usuario.unidadesventa).forEach((ku) => {
        defaultColumns["resumentodos-" + ku] =
          UAI.columnasReporteMatriculasPorDefecto
      })
      Object.keys(usuario.unidadesventa).forEach((ku) => {
        defaultColumns["resumen-" + ku] =
          UAI.columnasReporteMatriculasPorDefecto
      })
    } else if (tipoColumnas === "cohortes") {
      Object.values(usuario.views).forEach((view) => {
        defaultColumns[view.key] = UAI.vistas[view.key].fields
      })
    } else {
      throw new Error("Tipo de columnas inválido:" + tipoColumnas)
    }
    return defaultColumns
  }

  /**
   * Obtiene las columnas actuales que debería ver el usuario
   * @param {Object} usuario usuario activo
   * @param {String} tipoColumnas cohortes o matriculas
   * @returns arreglo de nombre de columnas
   */
  static obtieneColumnsActual = function (usuario, tipoColumnas) {
    // Caché local del browser
    let storage = window.localStorage
    // Obtenemos las columnas desde el caché
    let columns = storage.getItem("columns" + tipoColumnas)
    // Si no tenemos columnas, usamos las default y las guardamos en caché
    if (!columns || columns === "undefined" || columns === "null") {
      columns = UAI.obtieneDefaultsColumns(usuario, tipoColumnas)
      storage.setItem("columns" + tipoColumnas, JSON.stringify(columns))
    }
    // Intentamos parsear las columnas
    try {
      columns = JSON.parse(columns)
    } catch (e) {
      // Si falla el parsing usamos las columnas por defecto
      columns = UAI.obtieneDefaultsColumns(usuario, tipoColumnas)
      storage.setItem("columns" + tipoColumnas, JSON.stringify(columns))
    }
    if (typeof columns === "object") {
      let defaultColumns = UAI.obtieneDefaultsColumns(usuario, tipoColumnas)
      Object.keys(defaultColumns).forEach((ck) => {
        if (!columns[ck]) {
          columns[ck] = defaultColumns[ck]
        }
      })
      return columns
    } else {
      log.debug(columns)
      throw new Error("Valor de columns inválido")
    }
  }

  static cohorteNoVisibleReporteMatriculados = function (cohorte, usuario) {
    return (
      cohorte.año_inicio !== usuario.periodo ||
      cohorte.dictado === "No" ||
      cohorte.esborrador ||
      cohorte.año_inicio < usuario.periodo - 1 ||
      cohorte.año_inicio > usuario.periodo
    )
  }

  static alertasCohortesImpl = {
    preparacion: [
      (c) => {
        if (c.esborrador && c.fechainicio < new Date()) {
          return {
            icono: <MdDescription />,
            texto:
              "Cohorte es borrador pero su fecha de inicio ya pasó. Corrija fecha de inicio o borre el borrador.",
          }
        }
        return null
      },
      (c) => {
        if (isNaN(new Date(Date.parse(c.fecha_inicio)))) {
          return {
            icono: <MdCalendarToday />,
            texto: "Fecha de inicio inválida",
          }
        } else {
          return null
        }
      },
    ],
    planificacion: [],
    ejecucion: [
      (c) => {
        if (isNaN(new Date(Date.parse(c.fecha_termino)))) {
          return {
            icono: <MdCalendarToday />,
            texto:
              "Fecha de término inválida. Sin fecha de término no podemos enviar encuesta de salida, ni comenzar proceso de cierre y mejor continua.",
          }
        }
      },
    ],
    cierre: [],
  }
  static alertasCohorte = function (cohorte, etapa) {
    let alertasImpl = UAI.alertasCohortesImpl[etapa]
    let alertas = []
    alertasImpl.forEach((fa) => {
      let alerta = fa(cohorte)
      if (alerta) {
        alertas.push(alerta)
      }
    })
    return alertas
  }

  static agregaIdCohorte = function (
    cohorte,
    indices = null,
    nombreCohorteId = "CohorteId",
    nombreFacultadId = "FacultadId",
    cohorteOrig = null
  ) {
    if (cohorteOrig) {
      cohorte.MaestroId = cohorteOrig.MaestroId
      cohorte[nombreCohorteId] = cohorteOrig[nombreCohorteId]
      cohorte[nombreFacultadId] = cohorteOrig[nombreFacultadId]
    }
    if (
      cohorte.GradoId &&
      (!cohorte.gradoid || cohorte.gradoid === "Nombre columna no coincide")
    ) {
      cohorte.gradoid = cohorte.GradoId
    }
    if (cohorte.facultad_id < 0) {
      log.debug(cohorte)
      log.debug("WARN! Facultad id < 0! " + cohorte.facultad_id)
    }
    // Si el cohortes es un borrador
    if (
      parseInt(cohorte.MaestroId) > 0 &&
      (parseInt(cohorte[nombreCohorteId]) < 1 || !cohorte[nombreCohorteId])
    ) {
      let borradorid = parseInt(cohorte.MaestroId) * -1
      if (indices) {
        indices.push(borradorid)
      }
      cohorte.id = borradorid
      cohorte.programa_id = borradorid
      cohorte.facultad_id = cohorte[nombreFacultadId] // TODO: No viene Facultad en lista de campos
    } else {
      if (indices) {
        indices.push(cohorte[nombreCohorteId])
      }
      cohorte.id = cohorte[nombreCohorteId]
      cohorte.programa_id = cohorte[nombreCohorteId]
      cohorte.facultad_id = cohorte[nombreFacultadId]
    }
    // Parseamos fecha de inicio para no hacerlo más adelante en mil lugares
  }

  static cambiarTabTypeActual = function (nuevoTabType) {
    let storage = window.localStorage
    storage.setItem("tabType", nuevoTabType)
  }

  static completaAtributosCohorte = function (cohorig, periodo) {
    let coh = cohorig.CohortesSalida
    if (!coh) {
      log.error(periodo)
      log.error(cohorig)
      throw new Error("WEIRD! Cohorte no tiene info de CohortesSalida")
    }
    let fechaInicio = new Date(Date.parse(coh.fecha_inicio))
    if (isNaN(fechaInicio)) {
      fechaInicio = new Date(1970, 1, 1)
    }
    coh.año_inicio = fechaInicio.getFullYear()
    coh.rol = cohorig.Rol
    coh.maestroid = cohorig.MaestroId ? parseInt(cohorig.MaestroId) : 0
    coh.FacultadId = cohorig.FacultadId
    coh.Rol = cohorig.Rol
    coh.esborrador =
      (!cohorig.CohorteId || cohorig.CohorteId < 1) && coh.maestroid > 0
    coh.id = coh.esborrador ? coh.maestroid * -1 : cohorig.CohorteId
    coh.fechainicio = fechaInicio
    coh.Periodo = coh.año_inicio === periodo
    coh.esBenchmark = false
    coh.gradoid = parseInt(coh.gradoid)
    if (
      coh.unidadventa &&
      UAI.obtieneUnidadVentaPorNombrecorto(coh.unidadventa) !== undefined
    ) {
      coh.unidadVentaForzada = true
    } else {
      coh.unidadVentaForzada = false
    }
    if (!coh.facultad_id && cohorig.FacultadId) {
      coh.facultad_id = cohorig.FacultadId
    }
    coh.facultad_id = parseInt(coh.facultad_id)
    coh.unidadesventa = UAI.obtieneUnidadesVentaCohorte(coh)
    return coh
  }

  static obtieneGradosCohortes = function (_arregloCohortes) {
    let _tipos = []
    let _tipo
    Object.keys(_arregloCohortes).forEach((_cohorteKey) => {
      _tipo = _arregloCohortes[_cohorteKey].gradoid
      if (_tipos[_tipo] === undefined) {
        _tipos[_tipo] = {}
      }

      _tipos[_tipo][_cohorteKey] = {}
      _tipos[_tipo][_cohorteKey] = _arregloCohortes[_cohorteKey]
    })
    return _tipos
  }

  static nombresGradosFormularios = {
    1: "diplomados",
    2: "magister",
    3: "cursos",
    4: "mba",
    5: "doctorado",
    7: "seminarios",
    8: "charlas",
  }
  static estadosPostulanteColor = {
    Aceptado: "warning",
    "No Cumple Req.": "danger",
    Matriculado: "success",
    Rechazado: "danger",
    "Pendiente Firma": "success",
    "Aceptado Académico": "warning",
    "Por Entrevistar": "primary",
    Revisada: "secondary",
    "Sin Pago Postulación": "dark",
    Nuevo: "secondary",
    Retirado: "danger",
    "Retirado Post Matrícula": "danger",
    Borrador: "secondary",
    "Cambio Programa": "danger",
  }
  static estadosPostulanteShort = {
    Aceptado: "aceptado",
    "No Cumple Req.": "nocumplereq",
    Matriculado: "matriculado",
    Rechazado: "rechazado",
    "Pendiente Firma": "pendientefirma",
    "Aceptado Académico": "aceptadoacademico",
    "Por Entrevistar": "porentrevistar",
    Revisada: "revisada",
    "Sin Pago Postulación": "sinpago",
    Nuevo: "nuevo",
    Retirado: "retirado",
    "Retirado Post Matrícula": "retiradopostmatricula",
    Borrador: "borrador",
    "Cambio Programa": "cambioprograma",
  }
  static estadosPostulanteMobile = {
    Aceptado: "Acep",
    "No Cumple Req.": "SinReq",
    Matriculado: "Mat",
    Rechazado: "Rech",
    "Pendiente Firma": "PFi",
    "Aceptado Académico": "AcAcad",
    "Por Entrevistar": "Entr",
    Revisada: "Rev",
    "Sin Pago Postulación": "SnPago",
    Nuevo: "Nvo",
    Retirado: "Ret",
    "Retirado Post Matrícula": "RetPost",
    Borrador: "Borr",
    "Cambio Programa": "CbPrg",
  }
  static tiposDocumentos = [
    {
      TipoDocumentoId: 1,
      Nombre: "Certificado de Licenciatura Universitaria",
    },
    {
      TipoDocumentoId: 2,
      Nombre: "Título Profesional (Notarial)",
    },
    {
      TipoDocumentoId: 3,
      Nombre: "Currículum Vitae",
    },
    {
      TipoDocumentoId: 4,
      Nombre: "Carta del Superior",
    },
    {
      TipoDocumentoId: 5,
      Nombre: "Carnet de Identidad / Pasaporte",
    },
    {
      TipoDocumentoId: 6,
      Nombre: "Certificado de Nacimiento / Pasaporte",
    },
    {
      TipoDocumentoId: 7,
      Nombre: "Carta de Recomendación",
    },
    {
      TipoDocumentoId: 8,
      Nombre: "O.C. / Documento Patrocinio",
    },
    {
      TipoDocumentoId: 9,
      Nombre: "Carta de Compromiso Organización (si no es independiente)",
    },
    {
      TipoDocumentoId: 10,
      Nombre: "Concentración de Notas",
    },
    {
      TipoDocumentoId: 11,
      Nombre: "Foto Carnet",
    },
    {
      TipoDocumentoId: 12,
      Nombre: "Motivation Letter",
    },
    {
      TipoDocumentoId: 13,
      Nombre: "IELTS / TOEFL Scores",
    },
    {
      TipoDocumentoId: 14,
      Nombre: "Carta Motivacional para Becas",
    },
  ]
  static displayatributos = atributos.displayatributos

  static vistasCategorias = {
    Cohortes: {
      icon: <IconoCohorte background={false} />,
      subcategorias: [
        "Herramientas",
        "General",
        "Comercial",
        "Marketing",
        "Finanzas",
      ],
      showTitle: true,
    },
    Postulaciones: {
      icon: <IconoPostulante background={false} />,
      subcategorias: ["General"],
      showTitle: true,
    },
    Reportes: {
      icon: <IconoReporte background={false} />,
      subcategorias: ["Postulaciones"],
      showTitle: true,
    },
    Ejecución: {
      icon: <IconoContratos background={false} />,
      subcategorias: ["Postulaciones"],
      showTitle: true,
    },
    Calidad: {
      icon: <IconoCalidad background={false} />,
      subcategorias: ["General", "Encuestas", "Datos"],
      showTitle: true,
    },
    Configuración: {
      icon: <IconoConfiguracion />,
      subcategorias: ["Maestro Productos"],
      showTitle: true,
    },
  }
  static obtieneVistaAtributosObligatorios(_usuario) {
    let view = {
      category: "Cohortes",
      subcategory: "Herramientas",
      title: "Borradores",
      subtitle: "Cohortes que aún no llegan al paso publicado",
      external: true,
      fields: [],
      roles: [
        "Director Ejecutivo Postgrados",
        "Director de Ventas",
        "Marketing",
        "Finanzas",
      ],
    }
    Object.values(_usuario.atributos).forEach((atributo) => {
      if (atributo.Obligatorio) {
        view.fields.push(atributo.key)
      }
    })
    return view
  }

  /**
   * Genera el listado de atributos a utilizar en la app. Combina los atributos
   * del MP con los atributos sintéticos de la App.
   * @param {*} _atributos
   * @returns
   */
  static obtieneAtributos(_atributos) {
    // Salida de la función con la suma de atributos
    let atributosclean = {}

    // Cargamos todos los atributos sintéticos primero
    Object.values(atributos.atributossinteticos).forEach((_atributo) => {
      _atributo.esSintetico = true
      _atributos.push(_atributo)
    })

    let atributosSinCategoria = []
    _atributos.forEach((atributo) => {
      atributo.key = atributo.Codigo
      let atributojs = UAI.displayatributos[atributo.Codigo]
      if (atributojs !== undefined) {
        if (atributojs.display !== undefined)
          atributo.display = atributojs.display
        if (atributojs.displaymobile !== undefined)
          atributo.displaymobile = atributojs.displaymobile
        if (atributojs.titlemobile !== undefined)
          atributo.titlemobile = atributojs.titlemobile
        if (atributojs.input !== undefined) atributo.input = atributojs.input
        if (atributojs.Schema) atributo.Schema = atributojs.Schema
      }
      atributo.categoria = atributo.Categoria
      if (
        atributo.Codigo === "habilitado" ||
        atributo.Codigo === "Habilitado" ||
        atributo.Codigo === "tipo_producto_id" ||
        atributo.Codigo === "formulariov2"
      ) {
        atributo.categoria = "1. Información general"
      }
      if (!atributo.categoria) {
        atributosSinCategoria.push(atributo)
        atributo.categoria = "1. Información general"
      }
      atributosclean[atributo.Codigo] = atributo
    })
    if (atributosSinCategoria.length > 0) {
      log.debug(
        "Hay " +
        atributosSinCategoria.length +
        " atributo(s) sin categoría en el MP!"
      )
      log.debug(atributosSinCategoria)
    }
    return atributosclean
  }
  static buscarCohortesPorVista = function (query, vista, usuario) {
    // Validar parámetros de entrada
    if (!vista) {
      throw new Error("Vista inválida")
    }
    let _atributos = vista.fields
    if (vista.fields.indexOf("nombre") < 0) {
      _atributos.push("nombre")
    }
    if (vista.fields.indexOf("id") < 0) {
      _atributos.push("id")
    }
    if (vista.fields.indexOf("sigla") < 0) {
      _atributos.push("sigla")
    }
    if (!query) {
      return []
    }
    let _query = query.split(/\s+/)
    let _cohortes = Object.values(usuario.cohortes).filter((value) => {
      let _match = false
      if (!value.Periodo) {
        return false
      }
      _atributos.forEach((atributo) => {
        if (value[atributo] === null || value[atributo] === undefined) {
          return
        }
        let _submatch = true
        _query.forEach((q) => {
          _submatch =
            _submatch &&
            value[atributo] &&
            value[atributo].toString().toUpperCase().search(q.toUpperCase()) >=
            0
        })
        _match = _match || _submatch
      })
      return _match
    })
    return _cohortes
  }
  /**
   * Para un usuario obtiene el listado de vistas a mostrar en un DropDownMenu
   * @param {Object} usuario
   * @returns
   */
  static obtieneVistasCategorias = function (usuario) {
    let categoriestmp = {}
    usuario.views.forEach((view) => {
      if (view.navbar !== undefined && view.navbar === false) {
        return
      }
      if (!view.category || !UAI.vistasCategorias[view.category]) {
        log.debug(
          "Vista sin categoría o categoría sin información " +
          view.key +
          " # " +
          view.category
        )
        return
      }
      view.sortorder = UAI.vistasCategorias[
        view.category
      ].subcategorias.indexOf(view.subcategory)
      if (!categoriestmp[view.category]) {
        let sortorder = Object.keys(UAI.vistasCategorias).indexOf(view.category)
        categoriestmp[view.category] = {
          sort: sortorder,
          title: UAI.vistasCategorias[view.category].showTitle
            ? view.category
            : "",
          icon: UAI.vistasCategorias[view.category].icon,
          views: [],
        }
      }
      categoriestmp[view.category].views.push(view)
    })
    for (let idc in categoriestmp) {
      let category = categoriestmp[idc]
      if (category.views.length === 0) {
        delete categoriestmp[idc]
        continue
      }
      let cleanviews = []
      let lastsubcategory = ""
      category.views.sort(function (a, b) {
        return a.sortorder - b.sortorder
      })
      for (let id in category.views) {
        let view = category.views[id]
        if (lastsubcategory !== view.subcategory) {
          if (lastsubcategory !== "") {
            cleanviews.push({
              sortorder: view.sortorder,
              key: view.subcategory + "-divider",
              title: view.subcategory,
              divider: true,
            })
          }
          cleanviews.push({
            sortorder: view.sortorder,
            key: view.subcategory + "-header",
            title: view.subcategory,
            header: true,
          })
          lastsubcategory = view.subcategory
        }
        cleanviews.push(view)
      }
      category.views = cleanviews
    }
    let sorted = Object.values(categoriestmp)
    sorted.sort(function (a, b) {
      return a.sort - b.sort
    })
    return sorted
  }
  /**
   * Obtiene una vista del usuario activo a partir de su código
   * @param {Usuario} usuario el usuario activo
   * @param {String} codigoVista el nombre código de la vista
   * @returns la vista o null
   */
  static obtieneVistaUsuarioPorCodigo(usuario, codigoVista) {
    return usuario.views.find((v) => v.key === codigoVista)
  }
  /**
   *
   * @param {*} fecha
   * @param {*} periodo
   * @returns
   */
  static obtieneFechasReporte(fecha, periodo) {
    let today = new Date()
    if (fecha > today) {
      fecha = today
    }
    let ayer = new Date(fecha.getTime() - 1 * 24 * 60 * 60 * 1000)
    let hh = fecha.getHours()
    if (hh < 19) {
      fecha = ayer
    }
    let ayearago = new Date(
      fecha.getFullYear() - 1,
      fecha.getMonth(),
      fecha.getDate(),
      fecha.getHours(),
      fecha.getMinutes()
    )
    let lastweek = new Date()
    lastweek.setTime(fecha.getTime() - 3600 * 24 * 7 * 1000)
    return [fecha, lastweek, ayearago]
  }

  /**
   *
   * @param {*} fecha
   * @param {*} periodo
   * @returns
   */
  static obtieneFechasReporteRequest(fecha, periodo) {
    log.debug("Calculando las fechas para reporte")
    log.debug(fecha)
    log.debug(periodo)
    let [today, lastweek, ayearago] = UAI.obtieneFechasReporte(fecha, periodo)
    let output = [
      { fecha: today, periodo: periodo },
      { fecha: lastweek, periodo: periodo },
      { fecha: ayearago, periodo: periodo - 1 },
    ]
    log.debug(output)
    return output
  }

  static formatCurrency = function (n, x) {
    var re = "\\d(?=(\\d{" + (x || 3) + "})+" + (n > 0 ? "\\." : "$") + ")"
    return Number.toFixed(Math.max(0, ~~n)).replace(new RegExp(re, "g"), "$&,")
  }

  static obtieneValoresAtributo = function (
    atributo,
    cohorte,
    usuario,
    callbackSuccess,
    callbackError
  ) {
    let basePostula = UAI.basePostula()
    let url = atributo.Url.replace(/http.*api\/(.*)$/, basePostula + "$1")
    if (cohorte) {
      let tipoProductoId = UAI.obtieneTipoProducto(cohorte)
      url = url
        .replace("@FacultadId", cohorte.facultad_id)
        .replace("@GradoId", cohorte.GradoId)
        .replace("@TipoProductoId", tipoProductoId)
    }
    if (usuario) {
      url = url.replace("@Usuario", usuario.username)
    }
    UAI.servicioPostula(url, "GET", null, callbackSuccess, callbackError)
  }

  static servicioPostula = function (
    url,
    method,
    data,
    callbackSuccess,
    callbackError,
    noretry = false
  ) {
    let requestResponse = null
    const fetch_retry = (url, options, n) =>
      fetch(url, options).catch(function (error) {
        log.debug(
          "Servicio postula:" + url + " NoRetry:" + noretry + " Left:" + n
        )
        if (n === 1 || noretry) {
          log.error("Vemos el error en fetch_retry")
          throw error
        }
        log.debug("Reintentando fetch " + url + " - intento: " + (n - 1))
        return fetch_retry(url, options, n - 1)
      })
    const processjson = (response) => {
      requestResponse = response
      return response.json()
    }
    const processresult = (result) => {
      if (requestResponse && requestResponse.ok) {
        callbackSuccess(result)
      } else {
        if (
          requestResponse &&
          requestResponse.status === 400 &&
          (!requestResponse.ok && !Array.isArray(requestResponse))
        ) {
          log.error(result)
          callbackError(result)
        } else {
          callbackError(
            "Postula devolvió un " +
            requestResponse.status +
            " - " +
            requestResponse.statusText
          )
        }
      }
    }
    const catcherror = (error) => {
      retries.left--
      log.error(error)
      if (retries.left > 0) {
        log.debug(
          "Reintentando fetch! " +
          url +
          " en 1 segundo. Quedan " +
          retries.left +
          " intentos"
        )
        setTimeout(() => {
          fetch_retry(url, options, retries.left)
            .then(processjson)
            .then(processresult)
            .catch(catcherror)
        }, 1000)
      } else {
        callbackError(error)
      }
    }

    let username = "PanelOnline"
    let password = "2024P4NEL0NL1N3"
    let encodedAuth = Buffer.from(username + ":" + password).toString("base64")
    if (data !== null) {
      data = JSON.stringify(data)
    }
    /* 
    let options = {
      method: method, // *GET, POST, PUT, DELETE, etc.
      mode: process.env.REACT_APP_ENVIRONMENT === "dev" ? "no-cors" : "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "omit", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json",
        Authorization: "Basic " + encodedAuth,
      },
      redirect: "follow", // manual, *follow, error
      referrer:
        process.env.REACT_APP_ENVIRONMENT === "dev"
          ? "unsafe-url"
          : "no-referrer", // no-referrer, *client
      body: data, // body data type must match "Content-Type" header
    }*/
    let options = {
      method: method, // *GET, POST, PUT, DELETE, etc.
      mode: "cors",
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      credentials: "omit", // include, *same-origin, omit
      headers: {
        "Content-Type": "application/json",
        Authorization: "Basic " + encodedAuth,
      },
      redirect: "follow", // manual, *follow, error
      referrer: "no-referrer", // no-referrer, *client
      body: data, // body data type must match "Content-Type" header
    }

    let retries = { left: 5 }
    fetch_retry(url, options, retries.left)
      .then(processjson)
      .then(processresult)
      .catch(catcherror)
  }

  static obtieneHistoricoCohorte = function (
    cohorte,
    benchmark,
    callbackSuccess,
    callbackError
  ) {
    var request = []
    request.push({ cohorteid: cohorte })
    if (benchmark && benchmark > 0) {
      request.push({ cohorteid: benchmark })
    }
    UAI.servicioPostula(
      UAI.basePostula() + "PostCohorte/ObtenerEstadisticaCohorteHistorico",
      "POST",
      request,
      callbackSuccess,
      callbackError
    )
  }
  static obtieneHistoricoUnidadVenta = function (
    cohortes,
    periodo,
    unidadventa,
    callbackSuccess,
    callbackError
  ) {
    let request = []
    let loaded = {}
    cohortes.forEach((cohorte) => {
      if (loaded[cohorte.id] === undefined) {
        request.push({ cohorteid: cohorte.id })
        loaded[cohorte.id] = 1
      }
      if (
        cohorte.StatsPrev !== undefined &&
        cohorte.StatsPrev !== null &&
        cohorte.StatsPrev.Id > 0 &&
        loaded[cohorte.StatsPrev.Id] === undefined
      ) {
        request.push({ cohorteid: cohorte.StatsPrev.Id })
        loaded[cohorte.StatsPrev.Id] = 1
      }
    })
    UAI.servicioPostula(
      UAI.basePostula() + "PostCohorte/ObtenerEstadisticaCohorteHistorico",
      "POST",
      request,
      callbackSuccess,
      callbackError
    )
  }
  /**
   * Calcula si un usuario en particular tiene permiso para editar algún atributo de una vista en particular
   * @param {Usuario} usuario objeto usuario
   * @param {String} view objeto vista
   */
  static obtienePermisoEdicionVista = function (usuario, view) {
    let permiso = false
    for (let idxf in view.fields) {
      let field = view.fields[idxf]
      let atributo = usuario.atributos[field]
      if (!atributo) {
        log.error("ERROR GRAVE! Atributo no existe:" + field)
      } else {
        if (atributo.editable) {
          permiso = true
        }
      }
    }
    return permiso
  }

  /**
   * Obtiene unidade de venta y facultad por defecto para un usuario
   * @param {Object} usuario
   * @returns
   */
  static obtieneTabDefaults(usuario) {
    let uv = Object.values(usuario.unidadesventa)[0]
    let fac = Object.keys(uv.facultades)[0]
    if (!uv || !fac) {
      throw new Error(
        "El usuario no tiene unidad de venta o facultad por defecto! Wrong!"
      )
    }
    let output = { unidadventa: uv.key, facultad: fac }
    return output
  }

  static obtieneDirectoresDeProgramaPosibles(
    facultadId,
    callbackSuccess,
    callbackError
  ) {
    UAI.servicioPostula(
      UAI.basePostula() +
      "PostCohorte/ObtenerDirectoresDeProgramaPosibles?FacultadId=" +
      facultadId,
      "GET",
      null,
      callbackSuccess,
      callbackError
    )
  }

  static obtieneAtributosCohortesMismoPrograma(
    programaId,
    atributo,
    callbackSuccess,
    callbackError
  ) {
    UAI.servicioPostula(
      UAI.basePostula() + "PostCohorte/ObtenerAtributoCohortesMismoPrograma",
      "POST",
      {
        ProgramaId: programaId,
        CodigoAtributo: atributo,
      },
      callbackSuccess,
      callbackError
    )
  }

  static obtieneFechasDisponiblesCalendarioAcademico(
    periodo,
    callbackSuccess,
    callbackError
  ) {
    UAI.servicioPostula(
      UAI.basePostula() +
      "PostMaestroProducto/ObtenerFechasDisponiblesCalendarioAcademico?año=" +
      periodo,
      "GET",
      null,
      callbackSuccess,
      callbackError
    )
  }

  /**
   * Obtiene un arreglo de códigos de unidades de venta de un cohorte
   * @param {Cohorte} cohorte
   * @returns
   */
  static obtieneUnidadesVentaCohorte = function (cohorte) {
    let uvsCohorte = []
    let facultad =
      UAI.obtieneFacultadPorId(cohorte.facultad_id) ||
      UAI.obtieneFacultadPorId(cohorte.FacultadId)
    Object.keys(unidadesventa).forEach((uvk) => {
      let uv = unidadesventa[uvk]
      if (
        (cohorte.unidadVentaForzada &&
          cohorte.unidadventa === uv.nombrecorto) || // El cohorte está forzado a la Unidad de Admisión
        (uv.facultadesArray.indexOf(facultad.key) >= 0 &&
          uv.grados.indexOf(cohorte.gradoid) >= 0 &&
          !cohorte.unidadVentaForzada) // El cohorte pertenece a la Unidad de Admisión y no está forzado
      ) {
        uvsCohorte.push(uv.nombrecorto)
      }
    })
    return uvsCohorte
  }

  static highlight_string = function (string, substring) {
    if (!string || string === null) {
      return ""
    }
    let text = []
    string.split(/(<\w+>)/).forEach((sstring) => {
      if (sstring.search("<") >= 0) {
        log.debug(sstring + " ya está con highlight")
        text.push(sstring)
        return
      }
      let index = sstring.toUpperCase().search(substring.toUpperCase())
      let prefix = index < 0 ? sstring : sstring.slice(0, index)
      let postfix = index < 0 ? "" : sstring.slice(index + substring.length)
      let infix =
        index < 0
          ? ""
          : "<" + sstring.slice(index, index + substring.length) + ">"
      text.push(prefix + infix + postfix)
    })
    return text.join("")
  }

  static highlight = function (text, _query) {
    if (_query === null) {
      log.warn("Query es NULL!")
      return text
    }
    let highlights = _query.split(/\s+/)
    highlights.forEach((highlight) => {
      text = UAI.highlight_string(text, highlight)
    })
    let output = (
      <span>
        {text.split(/(<\w+>)/).map((part, idx) => {
          if (part.search("<") >= 0) {
            return (
              <span key={idx} className="highlight">
                {part.slice(1, part.length - 1)}
              </span>
            )
          } else {
            return part
          }
        })}
      </span>
    )
    return output
  }

  /**
   * Agrega un programa a Postula
   * @param {int} facultadid ID de la facultad
   * @param {int} gradoid ID del grado
   * @param {String} nombre Nombre del programa
   * @param {String} sigla Sigla del programa
   * @param {function} callbackSuccess Callback exitoso
   * @param {function} callbackError Callback si hay error
   */
  static agregarPrograma = function (
    facultadid,
    gradoid,
    nombre,
    sigla,
    callbackSuccess,
    callbackError
  ) {
    var requestPrograma = {
      FacultadId: facultadid,
      GradoId: gradoid,
      Sigla: sigla,
      Nombre: nombre,
    }

    UAI.servicioPostula(
      UAI.basePostula() + "PostPrograma/IngresarPrograma",
      "POST",
      requestPrograma,
      callbackSuccess,
      callbackError
    )
  }

  /**
   *
   */
  static limpiarCache = (url) => {
    log.info("Limpiando caché")
    localforage.clear().then(() => {
      let storage = window.localStorage
      storage.removeItem("columnscohortes")
      storage.removeItem("columnspostulantes")
      window.location.href = UAI.wwwroot() + url
    })
  }


  static agregarCohortesCache = async function (
    cohortesPorPeriodo,
    callbackSuccess,
    callbackError
  ) {
    let usuario = cohortesPorPeriodo[0].Usuario
    if (!usuario) {
      throw new Error("Usuario inválido")
    }
    log.debug("Agregando cohortes al caché de " + usuario)
    localforage
      .getItem("cohortes-" + usuario)
      .then((value) => {
        if (!value) {
          let _cohortesPorPeriodo = cohortesPorPeriodo.reduce(
            (prev, current) => {
              if (!prev[current.Periodo]) {
                prev[current.Periodo] = current
              }
              return prev
            },
            {}
          )
          value = {
            date: new Date(),
            data: _cohortesPorPeriodo,
          }
        } else {
          cohortesPorPeriodo.forEach((request) => {
            value.data[request.Periodo] = value.data[request.Periodo] || {
              Usuario: request.Usuario,
              Periodo: request.Periodo,
              Cohortes: [],
              Atributos: null,
            }
            request.Cohortes.forEach((c) => {
              if (!value.data[request.Periodo].Atributos) {
                value.data[request.Periodo].Atributos = Object.keys(c)
              }
              let cohorteEnCache = value.data[request.Periodo].Cohortes.find(
                (c2) => {
                  return c2.CohorteId === c.CohorteId
                }
              )
              if (!cohorteEnCache) {
                value.data[request.Periodo].Cohortes.push(c)
              } else {
                let indice = value.data[request.Periodo].Cohortes.findIndex(
                  (c2) => {
                    return c2.CohorteId === c.CohorteId
                  }
                )
                value.data[request.Periodo].Cohortes.splice(indice, 1)
                value.data[request.Periodo].Cohortes.push(c)
              }
            })
          })
          value.date = new Date()
        }
        localforage
          .setItem("cohortes-" + usuario, value)
          .then((result) => {
            callbackSuccess()
          })
          .catch((error) => {
            log.error(error)
            callbackError(error)
          })
      })
      .catch((error) => {
        log.error(error)
        callbackError(error)
      })
  }

  /**
   * Agrega al caché un conjunto de atributos definido para todos los cohortes de
   * un período específico.
   *
   * @param {String} usuario nombre del usuario
   * @param {Array} cohortes arreglo de cohortes a guardar
   * @param {Array} atributos arreglo de nombres de atributos a actualizar en el caché
   * @param {function} callbackSuccess callback de éxito
   * @param {function} callbackError callback de error
   */
  static agregarAtributosCohortesCache = function (
    usuario,
    cohortes,
    atributos,
    callbackSuccess,
    callbackError
  ) {
    log.debug("Agregando atributos a cohortes en el caché de " + usuario)
    log.debug(cohortes)
    log.debug(atributos)
    localforage
      .getItem("cohortes-" + usuario)
      .then((value) => {
        if (!value) {
          log.debug(
            "No existe caché mientras que estamos agregando atributos! Problema en carga inicial."
          )
          throw new Error(
            "No existe caché! No existe caché mientras que estamos agregando atributos! Problema en carga inicial."
          )
        }
        cohortes.forEach((res) => {
          res.Cohortes.forEach((cohorte) => {
            let cohorteEnCache = value.data[res.Periodo].Cohortes.find((c2) => {
              return (
                c2.CohorteId === cohorte.CohorteId ||
                c2.MaestroId === cohorte.MaestroId
              )
            })
            if (!cohorteEnCache) {
              log.debug(
                "Cohorte no encontrado en el caché a actualizar! ID:" +
                cohorte.CohorteId +
                "_"
              )
              return
            }
            atributos.forEach((a) => {
              cohorteEnCache.CohortesSalida[a] = cohorte.CohortesSalida[a]
            })
          })
          let atributosPorAgregar = atributos.filter(
            (a) => !value.data[res.Periodo].Atributos.includes(a)
          )
          value.data[res.Periodo].Atributos = value.data[
            res.Periodo
          ].Atributos.concat(atributosPorAgregar)
        })

        value.date = new Date()
        let newValue = JSON.parse(JSON.stringify(value))
        localforage
          .setItem("cohortes-" + usuario, newValue)
          .then((result) => {
            callbackSuccess()
          })
          .catch((error) => {
            log.error(error)
            callbackError(error)
          })
      })
      .catch((error) => {
        log.error(error)
        callbackError(error)
      })
  }
  /**
   * Obtiene el listado de cohortes para un usuario, utilizando el período solicitado y el anterior.
   *
   * @param {*} usuario
   * @param {*} periodo
   * @param {*} atributos
   * @param {*} callbackSuccess
   * @param {*} callbackError
   */
  static obtieneCohortesUsuario = function (
    usuario,
    periodo,
    atributos,
    periodounico,
    nocache,
    callbackProgress,
    callbackSuccess,
    callbackError
  ) {
    // Lista de todos los atributos del Maestro de Programas
    let listaAtributos = atributos
    if (listaAtributos.indexOf("habilitado") < 0) {
      listaAtributos.push("habilitado")
    }
    // Arreglo con los requests que haremos, pidiendo todos los atributos de cohortes para el período actual y el anterior
    let requestCohortes = [
      [{ Usuario: usuario, Periodo: periodo, Campos: listaAtributos }],
    ]
    if (!periodounico) {
      requestCohortes.push([
        { Usuario: usuario, Periodo: periodo - 1, Campos: listaAtributos },
      ])
      if (periodo < new Date().getFullYear()) {
        requestCohortes.push([
          {
            Usuario: usuario,
            Periodo: periodo + 1,
            Campos: listaAtributos,
          },
        ])
      }
      if (periodo > 2019) {
        requestCohortes.push([
          {
            Usuario: usuario,
            Periodo: periodo - 2,
            Campos: listaAtributos,
          },
        ])
      }
    }

    // Variables para guardar los resultados acumulados de cada request
    let results = {}
    let errors = []

    // Promesa asíncrona que se hace cargo de resolver si todo está  listo
    Promise.allSettled(
      // Por cada request creamos una promesa. Se invoca resolve cuando sale todo bien y reject cuando sale algo mal
      requestCohortes.map(
        (request) =>
          new Promise((resolve, reject) => {
            let _periodo = request[0].Periodo
            // Obtenemos los datos de caché
            localforage.getItem("cohortes-" + usuario).then((value) => {
              // Si existen datos
              if (value !== null && !nocache) {
                // Calculamos la edad de los datos
                let now = new Date()
                let diff = now - value.date
                // Calculamos si hay diferencial de atributos
                let _atributosPorCargar = atributos.filter(
                  (a) =>
                    value.data[_periodo] &&
                    !value.data[_periodo].Atributos.includes(a)
                )
                // Si la información de cohortes no tiene más de 24 horas, y tenemos todos los atributos, usamos la info del caché
                if (
                  (diff < 1000 * 60 * 60 * 24 ||
                    _periodo < now.getFullYear()) &&
                  value.data &&
                  value.data[_periodo] &&
                  _atributosPorCargar.length === 0
                ) {
                  log.info(
                    "Período " +
                    _periodo +
                    " leído desde caché, creado: " +
                    value.date
                  )
                  results[_periodo] = value.data[_periodo]
                  resolve()
                  return
                }
              }

              log.debug("Cargando período " + _periodo + " desde Postula")
              UAI.servicioPostula(
                UAI.basePostula() + "PostCohorte/ObtenerCohortesPorUsuario",
                "POST",
                request,
                (result) => {
                  results[_periodo] = result[0] || {
                    Usuario: usuario,
                    Periodo: _periodo,
                    Cohortes: [],
                  }
                  results[_periodo].Atributos = listaAtributos
                  resolve()
                },
                (error) => {
                  callbackProgress(0)
                  log.error(error)
                  errors.push(request)
                  reject(error)
                }
              )
            })
          })
      )
    )
      .then((allresults) => {
        let allok = true
        allresults.forEach((res) => {
          if (res.status !== "fulfilled") {
            allok = false
            log.error(res)
          }
        })
        if (allok) {
          callbackSuccess(Object.values(results))
        } else {
          log.error("Hubo errores cargando al usuario.")
          log.error(errors)
          callbackError(errors)
        }
      })
      .catch((error) => {
        callbackError(error)
      })
  }
  static obtieneCohortesBenchmarkHijos = function (usuario, cohorteId, depth) {
    if (depth > 10) {
      return []
    }
    let cohorte = usuario.cohortes[cohorteId]
    if (!cohorte) {
      throw new Error(
        "ID de cohorte inválido, el usuario no tiene acceso " + cohorteId
      )
    }
    let hijos = Object.values(usuario.cohortes).filter((c) => {
      return c.cohorte_padre_indicadores === cohorte.id
    })
    let cohortes = [cohorte]
    cohortes = cohortes.concat(hijos)
    let benchmark = cohorte.benchmarkinfo
    if (benchmark) {
      if (benchmark.id === cohorte.id) {
        log.debug("El cohorte recursivamente se tiene a si mismo de benchmark!")
      } else {
        let benchmarkHijos = UAI.obtieneCohortesBenchmarkHijos(
          usuario,
          benchmark.id,
          depth + 1
        )
        cohortes = cohortes.concat(benchmarkHijos)
      }
    }
    return cohortes
  }

  static obtieneEncuestasDocentePorAño = function (cohortes) {
    let encuestasDocentesPorAño = {}
    let procesadas = []
    let seccionesProcesadas = []
    log.debug(cohortes)
    cohortes
      .filter((c) => {
        return c.parsed_encuesta_docente
      })
      .forEach((cohorte) => {
        log.debug("Procesando encuesta docente de cohorte " + cohorte.id)
        if (
          procesadas.indexOf(
            cohorte.parsed_encuesta_docente[0]["Nombre periodo"]
          ) >= 0 ||
          cohorte.nombre.toLowerCase().match(/continuidad/i)
        ) {
          log.debug("Encuesta ya procesada")
          return
        }
        encuestasDocentesPorAño.encuestas_docentes =
          encuestasDocentesPorAño.encuestas_docentes || []
        encuestasDocentesPorAño.encuestas_docentes.push(cohorte.id)

        procesadas.push(cohorte.parsed_encuesta_docente[0]["Nombre periodo"])

        log.debug(
          "Agregando encuesta " +
          cohorte.parsed_encuesta_docente[0]["Nombre periodo"]
        )

        let _encuestas = cohorte.parsed_encuesta_docente
        let _año = 0
        let totalProfesor = 0
        let countProfesor = 0
        let totalAsignatura = 0
        let countAsignatura = 0
        _encuestas.sort((a, b) => {
          return (a["Apellido paterno"] + " " + a["Nombre"]).localeCompare(
            b["Apellido paterno"] + " " + b["Nombre"]
          )
        })
        let _encuestasClean = []
        _encuestas.forEach((encuesta) => {
          // TODO: Revisar se debemos ignorar por sección o por profesor+sección para NP y sección para NA
          if (seccionesProcesadas.indexOf(encuesta["Sección"]) >= 0) {
            return
          }
          _encuestasClean.push(encuesta)
          seccionesProcesadas.push(encuesta["Sección"])
        })
        _encuestasClean.forEach((encuesta, idx) => {
          // let np = parseFloat(encuesta["Nota profesor"].replace(",", "."))
          let np = parseFloat(encuesta["Nota profesor"])
          if (np > 0) {
            totalProfesor += np
            countProfesor++
            encuesta.np = np
          }
          let na = parseFloat(encuesta["Nota asignatura"])
          if (na > 0) {
            totalAsignatura += na
            countAsignatura++
            encuesta.na = na
          }
          encuesta.rowClass = ""
          if (
            (na > 0 && na.toFixed(1) < 5.5) ||
            (np > 0 && np.toFixed(1) < 5.5)
          ) {
            encuesta.rowClass = "alert-danger"
          }
          encuesta.año = parseInt(encuesta["Año"])
          if (encuesta.año > _año) {
            _año = encuesta.año
          }
        })
        encuestasDocentesPorAño[_año] = {
          profesor: (totalProfesor / countProfesor).toFixed(1),
          asignatura: (totalAsignatura / countAsignatura).toFixed(1),
        }
      })
    return encuestasDocentesPorAño
  }

  static creaCohorteRespuestaPostula = function (_cohorte) {
    let cohorte = JSON.parse(JSON.stringify(_cohorte))
    cohorte.Respuesta = "OK"
    if (!cohorte.ProgramaId && cohorte.programa_id) {
      cohorte.ProgramaId = cohorte.programa_id
    }
    if (!cohorte.GradoId && cohorte.gradoid) {
      cohorte.GradoId = cohorte.gradoid
    }
    if (!cohorte.FacultadId && cohorte.facultad_id) {
      cohorte.FacultadId = cohorte.facultad_id
    }
    cohorte.id = cohorte.ProgramaId
    cohorte.gradoid = cohorte.GradoId
    cohorte.habilitado = cohorte.Habilitado
    cohorte.MaestroId = 0
    if (!cohorte.FacultadId) {
      log.error(cohorte)
      throw new Error("Cohorte sin información de Facultad")
    }
    let newCohorte = {
      CohorteId: cohorte.ProgramaId,
      Rol: "[]",
      FacultadId: cohorte.FacultadId,
      CohortesSalida: cohorte,
    }

    return newCohorte
  }
  static obtieneUnidadesVentaUsuario = function (
    usuario,
    periodo,
    callbackSuccess,
    callbackError
  ) {
    let requestUsuario = [{ Usuario: usuario, Periodo: periodo }]

    UAI.servicioPostula(
      UAI.basePostula() + "PostUnidadVenta/ObtenerUnidadVentaActivasPorUsuario",
      "POST",
      requestUsuario,
      callbackSuccess,
      callbackError
    )
  }

  /**
   * Avanza un borrador a un nuevo paso
   * @param {String} usuario nombre de usuario
   * @param {object} cohorte objeto cohorte borrador
   * @param {function} callbackSuccess callback de éxito
   * @param {function} callbackError callback de error
   */
  static actualizarPasoBorrador = function (
    usuario,
    cohorte,
    callbackSuccess,
    callbackError
  ) {
    let [pasoActual, pasoSiguiente] = UAI.obtienePasoActualSiguiente(cohorte)
    if (!pasoActual || !pasoSiguiente) {
      log.debug(cohorte)
      log.debug(pasoActual)
      log.debug(pasoSiguiente)
      callbackError("No existe paso siguiente para cohorte")
      return
    }
    let requestUsuario = {
      Usuario: usuario,
      MaestroId: cohorte.id * -1,
      PasoSiguienteId: pasoSiguiente.id,
    }

    log.debug(requestUsuario)
    UAI.servicioPostula(
      UAI.basePostula() + "PostCohorte/ActualizarPasoCohorteBorrador",
      "POST",
      requestUsuario,
      callbackSuccess,
      callbackError
    )
  }

  /**
   * Obtiene el paso actual de un borrador y el paso siguiente
   * @param {object} cohorte objeto cohorte
   * @returns arreglo con paso actual y paso siguiente (de existir)
   */
  static obtienePasoActualSiguiente = function (cohorte) {
    if (!cohorte) {
      return [null, null]
    }
    let pasoActual = UAI.pasos.find((p) => p.id === cohorte.PasoActualId)
    let index = UAI.pasos.indexOf(pasoActual)
    let pasoSiguiente = UAI.pasos[index + 1]
    return [pasoActual, pasoSiguiente]
  }

  /**
   * Crea una notificación para despliegue en la interfaz
   * @param {Object} usuario
   * @param {String} mensaje
   * @param {Object} obj
   */
  static notificacionUsuario = function (usuario, mensaje, obj = undefined) {
    usuario.notificaciones = usuario.notificaciones || []
    usuario.notificaciones.push({ mensaje: mensaje, objeto: obj })
  }
  /**
   * Obtiene los detalles de un arreglo de cohortes por su ID. El conjunto de atributos es el que
   * está en la configuración general. Se agregan GradoId y FacultadId a los atributos a pedir.
   *
   * @param {*} cohortes
   * @param {*} callbackSuccess
   * @param {*} callbackError
   */
  static obtieneDetallesCohortesPorId = function (
    cohortes,
    callbackSuccess,
    callbackError,
    atributos
  ) {
    let requestCohortes = {
      Campos: atributos,
    }

    cohortes.forEach((c) => {
      if (c > 0) {
        requestCohortes.Cohortes = requestCohortes.Cohortes || []
        requestCohortes.Cohortes.push(c)
      } else {
        requestCohortes.Maestros = requestCohortes.Maestros || []
        requestCohortes.Maestros.push(c * -1)
      }
    })
    UAI.servicioPostula(
      UAI.basePostula() + "PostCohorte/ObtenerCohortePorCohorteId",
      "POST",
      requestCohortes,
      callbackSuccess,
      callbackError
    )
  }

  static agregarDetallesCohorteUsuario = (usuario, detalles, result = []) => {
    log.debug(detalles.length + " cohortes pendientes obtenidos")
    let cohortesSinFechaValida = []
    detalles.forEach((cohorte) => {
      let fechainicio = cohorte.fecha_inicio
      if (fechainicio !== null && fechainicio.indexOf("T00:00:00") >= 0) {
        fechainicio = fechainicio.slice(0, 10)
      }
      let periodo = new Date(Date.parse(fechainicio)).getFullYear()
      if (isNaN(periodo)) {
        cohortesSinFechaValida.push(cohorte)
        periodo = 1970
      }
      let newCohorte = UAI.creaCohorteRespuestaPostula(cohorte)
      let prevResult = result.find((element) => {
        return (
          element !== undefined &&
          element.Usuario === usuario.username &&
          element.Periodo === usuario.periodo
        )
      })
      if (prevResult === undefined) {
        result.push({
          Usuario: usuario.username,
          Periodo: usuario.periodo,
          Cohortes: [newCohorte],
          Atributos: UAI.atributosMinimos,
        })
      } else {
        prevResult.Cohortes.push(newCohorte)
      }
    })
    if (cohortesSinFechaValida.length > 0) {
      UAI.notificacionUsuario(
        usuario,
        "Hay " +
        cohortesSinFechaValida.length +
        " cohortes sin fecha de inicio válida",
        cohortesSinFechaValida
      )
    }
  }

  static sumaPreguntasNumerica = (preguntaOrigen1, preguntaOrigen2) => {
    let pregunta1 = JSON.parse(JSON.stringify(preguntaOrigen1))
    pregunta1.avg =
      (pregunta1.avg * pregunta1.respuestas +
        preguntaOrigen2.avg * preguntaOrigen2.respuestas) /
      (pregunta1.respuestas + preguntaOrigen2.respuestas)
    pregunta1.max = Math.max(pregunta1.max, preguntaOrigen2.max)
    pregunta1.min = Math.min(pregunta1.min, preguntaOrigen2.min)
    pregunta1.respuestas += preguntaOrigen2.respuestas
    let distribucion = pregunta1.distribucion.concat(
      preguntaOrigen2.distribucion
    )
    let nuevaDistribucion = []
    while (distribucion.length > 0) {
      let distro1 = distribucion.pop()
      let distro2idx = distribucion.findIndex((d) => {
        return distro1.x === d.x
      })
      if (distro2idx >= 0) {
        let distro2 = distribucion[distro2idx]
        distro1 = { x: distro1.x, Freq: distro1.Freq + distro2.Freq }
        distribucion.splice(distro2idx, 1)
      }
      nuevaDistribucion.push(distro1)
    }
    pregunta1.distribucion = nuevaDistribucion
    let nuevaMediana = undefined
    let total = 0
    pregunta1.distribucion.forEach((distro) => {
      total += distro.Freq
      if (total > pregunta1.respuestas / 2 && nuevaMediana === undefined) {
        nuevaMediana = distro.x
      }
    })
    pregunta1.mediana = parseInt(nuevaMediana)
    return pregunta1
  }

  static sumaPreguntasTexto = (preguntaOrigen1, preguntaOrigen2) => {
    let pregunta = JSON.parse(JSON.stringify(preguntaOrigen1))
    let p1 = Array.isArray(pregunta.respuestas) ? pregunta.respuestas : []
    let p2 = Array.isArray(preguntaOrigen2.respuestas)
      ? JSON.parse(JSON.stringify(preguntaOrigen2.respuestas))
      : []
    pregunta.respuestas = p1.concat(p2)
    return pregunta
  }

  static sumaEncuestas = (encuesta1, encuesta2) => {
    if (!encuesta1 && encuesta2) {
      return encuesta2
    }
    if (!encuesta2 && encuesta1) {
      return encuesta1
    }
    if (!encuesta2 && !encuesta1) {
      return undefined
    }
    log.debug(
      "Sumando encuestas " + encuesta1.nombre + " + " + encuesta2.nombre
    )
    Object.keys(encuesta1.preguntas).forEach((preguntaKey) => {
      if (!encuesta2.preguntas[preguntaKey]) {
        log.debug("Pregunta no existe en la segunda encuesta")
        return
      }
      if (encuesta1.preguntas[preguntaKey].tipo === "numerica") {
        encuesta1.preguntas[preguntaKey] = UAI.sumaPreguntasNumerica(
          encuesta1.preguntas[preguntaKey],
          encuesta2.preguntas[preguntaKey]
        )
      } else if (encuesta1.preguntas[preguntaKey].tipo === "texto") {
        encuesta1.preguntas[preguntaKey] = UAI.sumaPreguntasTexto(
          encuesta1.preguntas[preguntaKey],
          encuesta2.preguntas[preguntaKey]
        )
      } else {
        log.debug("Tipo de pregunta desconocido cohorte " + encuesta1.nombre)
      }
    })
    return encuesta1
  }

  static obtieneProgramasFiltros = function (
    facultadId,
    gradoId,
    usuario,
    callbackSuccess,
    callbackProgress,
    callbackError
  ) {
    let request = {
      FiltroFacultad: facultadId,
      FiltroGrado: gradoId,
      Usuario: usuario,
    }
    log.debug(request)
    UAI.servicioPostula(
      UAI.basePostula() + "PostPrograma/ObtenerProgramas",
      "POST",
      request,
      (result) => {
        callbackSuccess(result)
      },
      (error) => {
        log.error(error)
        callbackError(error)
      }
    )
  }

  static obtieneProgramaCohortes = function (
    programaId,
    usuario,
    callbackSuccess,
    callbackProgress,
    callbackError
  ) {
    // Obtiene datos del programa desde json en duro TODO: Cambiar por datos desde Postula
    let programaFromData = programasData[programaId]
    if (!programaFromData) {
      callbackError("No tenemos información del programa padre! " + programaId)
      return
    } else {
      programaFromData.cohortes = []
    }
    log.debug("Cargando cohortes del programa " + programaFromData.nombre)

    UAI.obtieneCohortesActivosPrograma(
      [programaId],
      (result) => {
        let sindatos = []
        result.forEach((r) => {
          let cohorte = usuario.cohortes[r]
          if (cohorte) {
            programaFromData.cohortes.push(cohorte)
          } else {
            sindatos.push(r)
          }
        })
        if (sindatos.length > 0) {
          log.debug("Cohortes sin datos cargados")
          log.debug(sindatos)
        }
        callbackSuccess(programaFromData)
      },
      (error) => {
        console.error(error)
        callbackError(error)
      }
    )
  }

  static obtieneCohortesActivosPrograma = function (
    programasids,
    callbackSuccess,
    callbackError
  ) {
    let requests = []
    programasids.forEach((programaPadreId) => {
      requests.push({
        FacultadId: null,
        TipoProductoId: null,
        ProgramaPadreId: programaPadreId,
      })
    })
    const results = []
    Promise.allSettled(
      // Por cada request creamos una promesa. Se invoca resolve cuando sale todo bien y reject cuando sale algo mal
      requests.map(
        (request) =>
          new Promise((resolve, reject) => {
            UAI.servicioPostula(
              UAI.basePostula() +
              "PostCohorte/ObtenerListaCohortesPorFacultadIdPorTipoProducto",
              "POST",
              request,
              (result) => {
                result.forEach((r) => {
                  results.push(r.ProgramaId)
                })
                resolve()
              },
              (error) => {
                log.error(error)
                reject()
              }
            )
          })
      )
    )
      .then((allresults) => {
        let allok = true
        allresults.forEach((res) => {
          if (res.status !== "fulfilled") {
            allok = false
          }
        })
        if (allok) {
          callbackSuccess(results)
        }
      })
      .catch((error) => {
        callbackError(error)
      })
  }
  /**
   * Obtiene todas las unidades de venta activas para una fecha específica y un
   * usuario en particular.
   * @param {*} usuario
   * @param {*} callbackSuccess
   * @param {*} callbackError
   */
  static obtieneUnidadesVentaPorUsuario = function (
    usuario,
    callbackSuccess,
    callbackError
  ) {
    let request = [{ Usuario: usuario.username, Fecha: new Date() }]
    UAI.servicioPostula(
      UAI.basePostula() + "PostUnidadVenta/ObtenerUnidadVentaActivasPorUsuario",
      "POST",
      request,
      callbackSuccess,
      callbackError
    )
  }
  static obtieneDetallesCohortesPorNombreCorto = function (
    cohortes,
    periodo,
    atributos,
    callbackSuccess,
    callbackError
  ) {
    var datacohortes = []
    for (var idx in cohortes) {
      var cohorte = cohortes[idx]
      var fechainicio = new Date(Date.parse(cohorte.FechaInicio))
      if (fechainicio.getFullYear() === periodo) {
        datacohortes.push(cohorte.nombrecorto)
      }
    }
    var requestCohortes = {
      NombresCortos: datacohortes,
      Campos: Object.keys(atributos),
    }

    UAI.servicioPostula(
      UAI.basePostula() + "PostCohorte/ObtenerCohortePorNombreCorto",
      "POST",
      requestCohortes,
      callbackSuccess,
      callbackError
    )
  }

  static obtieneCohortesPorNombreCorto = function (
    nombrecorto,
    atributos,
    callbackSuccess,
    callbackError
  ) {
    let requestCohortes = {
      NombresCortos: [nombrecorto],
      Campos: atributos,
    }

    UAI.servicioPostula(
      UAI.basePostula() + "PostCohorte/ObtenerCohortePorNombreCorto",
      "POST",
      requestCohortes,
      callbackSuccess,
      callbackError
    )
  }

  static obtienePlanesDeCapacitacion = function (
    planes,
    periodo,
    callbackSuccess,
    callbackError
  ) {
    let requestCohortes = {
      "PlanesDeCapacitacionId": Object.keys(planes),
      "SoloSence": false,
      "Año": periodo,
    }

    log.debug(requestCohortes)
    UAI.servicioPostula(
      UAI.basePostula() + "PostPlanesDeCapacitacion/ObtenerPlanesDeCapacitacion",
      "POST",
      requestCohortes,
      callbackSuccess,
      callbackError
    )
  }

  static obtienePostulantesCohorte = function (
    cohorteid,
    callbackSuccess,
    callbackError
  ) {
    log.info("Obtiene alumnos cohorte " + cohorteid)
    let request = { pagina: 1, MaximoPorPagina: 99999, cohortes: [cohorteid] }
    UAI.servicioPostula(
      UAI.basePostula() +
      "PostPostulacion/ObtenerPostulacionesCohortePorCohorteid",
      "POST",
      request,
      callbackSuccess,
      callbackError
    )
  }

  static obtienePostulantesUsuario = function (
    usuario,
    orderby,
    pagina,
    filtro,
    callbackSuccess,
    callbackError
  ) {
    log.info("Obtiene estudiantes cohortes de usuario " + usuario.username)
    let filtroNombre = filtro.criterio === "Nombre" ? filtro.palabra : ""
    let filtroNumeroDocumento =
      filtro.criterio === "RUT/DNI" ? filtro.palabra : ""
    let request = {
      FiltroPostulanteNombre: filtroNombre,
      FiltroNumeroDocumento: filtroNumeroDocumento,
      pagina: pagina,
      MaximoPorPagina: 50,
      Usuario: usuario.username,
      TamanoPagina: 50,
      NumeroPagina: pagina,
      Orden: orderby,
    }
    if (filtro.soloEntrevistas) {
      request.FiltroEstadoNombre = "Por Entrevistar"
    }
    if (filtro.Año) {
      request.Ano = usuario.periodo
    }
    UAI.servicioPostula(
      UAI.basePostula() + "PostPostulacion/ObtenerPostulacionPorFiltros",
      "POST",
      request,
      callbackSuccess,
      callbackError
    )
  }

  /**
   * Ordena un arreglo de cohortes de acuerdo a período, grado y nombre.
   * Opcionalmente puede agregar el campo de agrupación para reporte.
   * @param {Cohorte} cohortes
   * @param {Integer} periodo
   * @param {Boolean} agrupar atributo del cohorte por el cual se ordenará primero
   * @returns
   */
  static ordenaCohortes = function (cohortes, periodo, agrupar = undefined) {
    log.debug(
      "Ordenando cohortes para reporte de matrículas. Agrupación:" + agrupar
    )
    return cohortes.sort((a, b) => {
      let sa =
        periodo -
        a.año_inicio +
        "-" +
        UAI.ordenGrados[a.gradoid] +
        "-" +
        a.nombre
      let sb =
        periodo -
        b.año_inicio +
        "-" +
        UAI.ordenGrados[b.gradoid] +
        "-" +
        b.nombre
      if (agrupar) {
        sa = a[agrupar] + "-" + sa
        sb = b[agrupar] + "-" + sb
      }
      return sa.localeCompare(sb)
    })
  }

  static obtieneIndicadorPregunta = function (pregunta) {
    /** Buscamos el indicador al que se refiere la pregunta en el diccionario de indicadores */
    let indicadorPregunta = undefined
    Object.keys(UAI.diccionarioEncuestas).forEach((indicadorKey) => {
      let versionesPreguntas = UAI.diccionarioEncuestas[indicadorKey].preguntas
      if (
        versionesPreguntas.indexOf(pregunta) >= 0 ||
        versionesPreguntas.indexOf("¿" + pregunta + "?") >= 0
      ) {
        indicadorPregunta = indicadorKey
      }
    })
    return indicadorPregunta
  }
  /**
   * Obtiene la configuración inicial de la aplicación, tanto desde el local storage como de Postula.
   * @param {*} callbackSuccess
   * @param {*} callbackError
   * @param {*} callbackProgress
   */
  static obtieneConfiguracionInicial = function (
    callbackSuccess,
    callbackError,
    callbackProgress
  ) {
    log.debug("Obteniendo configuración inicial de aplicación")
    const results = {
      usuarios: users,
      facultades: facultades,
      unidadesventa: unidadesventa,
    }
    localforage.getItem("atributos").then((value) => {
      if (value !== undefined && value !== null) {
        let atributos = UAI.obtieneAtributos(value)
        results["atributos"] = atributos
        log.debug(
          "Obtuvimos " +
          Object.keys(atributos).length +
          " atributos de cohortes desde el caché"
        )
        UAI.configuracion = results
        callbackSuccess(results)
      } else {
        const pending = [1]
        const progress = () => {
          callbackProgress()
          if (pending.length > 0) {
            window.setTimeout(progress, 1000)
          }
        }
        window.setTimeout(progress, 1000)
        UAI.obtieneAtributosCohortes(
          (result) => {
            pending.pop()
            let rawAtributos = JSON.parse(JSON.stringify(result))
            let atributos = UAI.obtieneAtributos(result)
            results["atributos"] = atributos
            localforage.setItem("atributos", rawAtributos).then(() => {
              log.debug(
                "Guardando " +
                Object.keys(atributos).length +
                " atributos de cohortes en caché"
              )
              callbackProgress()
              if (pending.length === 0) {
                UAI.configuracion = results
                callbackSuccess(results)
              }
            })
          },
          (error) => {
            log.error("Error obteniendo atributos del Maestro de Programas!")
            log.error(error)
            callbackError(error)
          }
        )
      }
    })
  }
  /**
   * Obtiene la fecha actual en formato específico con separador
   * @param {String} separator separador para fecha
   * @returns
   */
  static getCurrentDate = function (separator = "-") {
    let newDate = new Date()
    let date = newDate.getDate()
    let month = newDate.getMonth() + 1
    let year = newDate.getFullYear()

    return `${year}${separator}${month < 10 ? `0${month}` : `${month}`
      }${separator}${date}`
  }

  /**
   * Dibuja una línea delimitador en la consola
   */
  static logLineaDelimitador = function () {
    log.info("-------------------------------------------------")
  }

  /**
   * Dibuja una línea delimitador en la consola
   */
  static logLineaDelimitadorGruesa = function () {
    log.info("=================================================")
  }

  static obtieneUsuarioInicial = function (nombreUsuarioAutenticado) {
    // Storage local básico
    let storage = window.localStorage

    let usuarioInicial = nombreUsuarioAutenticado
    // Si el usuario tiene permiso para impersonar a otro usuario, revisar si está guardado en el storage
    if (UAI.permisosImpersonar[nombreUsuarioAutenticado] !== undefined) {
      // Obtenemos el último usuario que impersonó
      let usuarioImpersonar = storage.getItem("impersonar")
      if (
        usuarioImpersonar !== undefined &&
        usuarioImpersonar !== null &&
        UAI.permisosImpersonar[nombreUsuarioAutenticado].forbidden.indexOf(
          usuarioImpersonar
        ) < 0 && // Que no esté prohibido impersonar a ese usuario en particular
        nombreUsuarioAutenticado !== usuarioImpersonar
      ) {
        usuarioInicial = usuarioImpersonar
      }
    }
    if (usuarioInicial !== nombreUsuarioAutenticado) {
      log.info("Impersonando a " + usuarioInicial)
    }
    return usuarioInicial
  }

  /**
   * Devuelve el período por defecto para la App
   * @param {String} nombreUsuario Nombre del usuario
   * @returns Integer año del período por defecto
   */
  static obtienePeriodoPorDefecto = function (nombreUsuario) {
    // Año actual, pero si ya estamos en septiembre año siguiente
    let today = new Date()
    let año = today.getFullYear()
    if (today.getMonth() >= 9) {
      año++
    }

    // Revisamos si el usuario tiene guardado un período desde su última sesión

    // Storage local básico
    let storage = window.localStorage

    if (storage.getItem("periodo-" + nombreUsuario)) {
      let _periodoStorage = parseInt(
        storage.getItem("periodo-" + nombreUsuario)
      )
      if (
        !isNaN(_periodoStorage) &&
        _periodoStorage >= 2012 &&
        _periodoStorage < año + 2
      ) {
        año = _periodoStorage
      }
    }

    return año
  }
  /**
   * Indica si un cohorte no cuenta con una medición
   * @param {Cohorte} cohorte Objeto cohorte
   * @param {String} encuesta Código encuesta
   * @returns
   */
  static cohorteSinMedicionEncuesta = function (cohorte, encuesta) {
    const valoresSinMedicion = ["NA", "N/A", "SIN MEDICIÓN", "NO SE MIDIÓ"]
    if (!cohorte[encuesta]) {
      return false
    }
    let valorEncuesta = cohorte[encuesta].toString().toUpperCase()
    return valoresSinMedicion.indexOf(valorEncuesta) >= 0
  }

  /**
   * Indica si un cohorte no cuenta con una medición
   * @param {Cohorte} cohorte Objeto cohorte
   * @param {String} encuesta Código encuesta
   * @returns
   */
  static cohorteSinDatosEncuesta = function (cohorte, encuesta) {
    return !(
      cohorte[encuesta] !== null &&
      cohorte[encuesta] !== "" &&
      cohorte[encuesta] !== 0 &&
      cohorte[encuesta] !== "0" &&
      !cohorte.esborrador
    )
  }

  /**
   * Revisa si las estadísticas obtenidas desde el caché están vigentes
   * o no.
   * @param {Object} statsFromCache conjunto de estadísticas obtenidas desde el caché
   * @returns true si las estadísticas todavía están vigentes
   */
  static cacheEstadisticasMatriculasVigente = function (statsFromCache) {
    if (!statsFromCache || !statsFromCache.date) {
      return false
    }
    let hoy = new Date()
    let ayer = new Date(hoy.getTime() - 1 * 24 * 60 * 60 * 1000)
    let fecha = hoy.getHours() < 19 ? ayer : hoy
    let last7pm = new Date(
      fecha.getFullYear(),
      fecha.getMonth(),
      fecha.getDate(),
      19,
      0,
      0
    )
    let diff = last7pm - statsFromCache.date
    return diff < 0
  }

  /**
   * Obtiene estadísticas de postulaciones y matriculados de todos los cohortes que tiene el usuario
   * o de un listado específico. Se encarga de guardar en caché los datos obtenidos para la fecha.
   * @param {*} usuario
   * @param {*} callbackSuccess
   * @param {*} callbackError
   * @param {*} callbackProgress
   */
  static obtieneEstadisticasCohortesPorFecha = function (
    usuario,
    listadoCohortes,
    listadoFechas,
    callbackSuccess,
    callbackError,
    callbackProgress
  ) {
    if (
      !listadoCohortes ||
      !listadoFechas ||
      !Array.isArray(listadoCohortes) ||
      !Array.isArray(listadoFechas)
    ) {
      throw new Error(
        "Parámetros inválidos para conseguir estadísticas de cohortes"
      )
    }
    const cachename = "stats-" + usuario.username
    log.debug("Obteniendo estadísticas de " + cachename)

    let cohortesPorCargar = {}
    listadoCohortes
      .filter((c) => {
        return !c.esborrador
      })
      .forEach((cohorte) => {
        listadoFechas.forEach((fecha) => {
          if (cohorte.id === 2916) {
            log.debug(
              "Decidiendo estadística a cargar para cohorte " +
              cohorte.id +
              " entre " +
              cohorte.año_inicio +
              " <-> " +
              fecha.periodo
            )
          }
          if (cohorte.año_inicio === fecha.periodo) {
            cohortesPorCargar[cohorte.id] = cohortesPorCargar[cohorte.id] || {
              Cohorte: cohorte,
              Fechas: [],
            }
            let fechaYMD = UAI.formatoFechaYMD(fecha.fecha)
            if (cohortesPorCargar[cohorte.id].Fechas.indexOf(fechaYMD) < 0) {
              cohortesPorCargar[cohorte.id].Fechas.push(fechaYMD)
            }
          }
        })
      })
    // Listado de cohortes solicitados que no tenían datos en caché
    callbackProgress(1)
    localforage.getItem(cachename).then((value) => {
      if (
        value &&
        UAI.cacheEstadisticasMatriculasVigente(value) &&
        value.data
      ) {
        // TODO: Filtrar si viene un listado de cohortes, lo que puede provocar que haya que igual
        // ir a buscar datos o bien devolver lo que está en caché
        let cohortesConDatos = []
        let cohortesPorCargarClean = {}
        Object.values(cohortesPorCargar).forEach((cohorte) => {
          cohorte.Fechas.forEach((fecha) => {
            let datoCache = value.data.cohortes[cohorte.Cohorte.id]
              ? value.data.cohortes[cohorte.Cohorte.id][fecha]
              : undefined
            if (datoCache) {
              cohortesConDatos.push(datoCache)
            } else {
              cohortesPorCargarClean[
                cohorte.Cohorte.id
              ] = cohortesPorCargarClean[cohorte.Cohorte.id] || {
                Cohorte: cohorte.Cohorte,
                Fechas: [],
              }
              cohortesPorCargarClean[cohorte.Cohorte.id].Fechas.push(fecha)
            }
          })
        })
        cohortesPorCargar = cohortesPorCargarClean
        log.debug(
          "De los " +
          listadoCohortes.length +
          " cohortes solicitados, hay " +
          Object.keys(cohortesPorCargar).length +
          " sin información para alguna de las fechas solicitadas"
        )
        if (Object.values(cohortesPorCargar).length === 0) {
          log.info(
            "Estadísticas obtenidas para " +
            cohortesConDatos.length +
            " cohortes"
          )
          let stats = new UAIStats()
          stats.initalize(cohortesConDatos, usuario, callbackProgress(100))
          if (stats.descartados && stats.descartados.length > 0) {
            log.debug("Cohortes descartados")
            log.debug(stats.descartados)
          }
          callbackSuccess(stats)
          return
        }
      }

      let cohortesPorFecha = Object.values(cohortesPorCargar).reduce(
        (prev, current) => {
          current.Fechas.forEach((fecha) => {
            prev[fecha] = prev[fecha] || []
            prev[fecha].push(current.Cohorte.id)
          })
          return prev
        },
        {}
      )

      log.debug(cohortesPorFecha)

      let requests = []
      let ignorados = []
      Object.keys(cohortesPorFecha).forEach((fecha) => {
        let num = parseInt(cohortesPorFecha[fecha].length) / 5
        for (let i = 0; i < cohortesPorFecha[fecha].length; i += num) {
          let chunk = cohortesPorFecha[fecha].slice(i, i + num)
          requests.push({
            Cohortes: chunk,
            FechaEstadistica: fecha,
            Campos: [
              "CohorteId",
              "Fecha",
              "Matriculados",
              "Postulantes",
              "Aceptados",
              "AceptadoAcademicamente",
              "Equivalentes",
              "Borrados",
              "PorEntrevistar",
              "SinPagoFeePostulacion",
              "Retirados",
              "PendienteFirma",
              "RetiradoPostMatricula",
              "RetiradoSinMatricula",
              "ContratosVigentesEquivalentes",
            ],
          })
        }
      })
      log.info("Cohortes ignorados: " + ignorados.length)
      if (Object.values(cohortesPorCargar).length > 0) {
        log.info(
          "Cohortes sin info: " + Object.values(cohortesPorCargar).length
        )
      }
      log.info(
        "Pediremos " +
        requests.length +
        " estadísticas de " +
        Object.values(cohortesPorCargar).length +
        " cohortes"
      )
      // Variables para guardar los resultados
      const totals = { totalresult: [], pending: requests.length }
      const pendingErrors = []
      Promise.allSettled(
        requests.map(
          (request) =>
            new Promise((resolve, reject) => {
              UAI.servicioPostula(
                UAI.basePostula() +
                "PostCohorte/ObtenerEstadisticaCohortePorFecha",
                "POST",
                request,
                (result) => {
                  totals.pending--
                  totals.totalresult = totals.totalresult.concat(result)
                  log.debug(totals.pending)
                  callbackProgress(
                    (
                      ((requests.length - totals.pending) / requests.length) *
                      100
                    ).toFixed(0)
                  )
                  resolve()
                },
                (error) => {
                  pendingErrors.push(request)
                  reject()
                }
              )
            })
        )
      )
        .then((allresults) => {
          let allok = true
          allresults.forEach((res) => {
            if (res.status !== "fulfilled") {
              allok = false
            }
          })
          if (allok) {
            let dataToStore = null
            if (
              Object.values(cohortesPorCargar).length > 0 &&
              value &&
              value.data
            ) {
              log.debug(totals.totalresult)
              dataToStore = UAI.mergeStatsObjeto(
                totals.totalresult,
                value.data,
                cohortesPorCargar
              )
            } else {
              dataToStore = UAI.obtieneStatsObjeto(
                totals.totalresult,
                cohortesPorCargar
              )
            }
            if (!dataToStore) {
              log.error(dataToStore)
              throw new Error("Datos de stats generados para caché inválidos")
            }
            localforage
              .setItem(cachename, {
                date: new Date(),
                data: dataToStore,
              })
              .then(() => {
                console.log("Estadísticas obtenidas")
                let stats = new UAIStats()
                stats.initalize(
                  totals.totalresult,
                  usuario,
                  callbackProgress(100)
                )
                console.log("Cohortes descartados")
                console.log(stats.descartados)
                callbackSuccess(stats)
              })
          } else {
            log.error(
              "Hubo errores cargando estadísticas, podríamos reintentar aquí"
            )
            log.error(pendingErrors)
            callbackError(pendingErrors)
          }
        })
        .catch((error) => {
          log.error(error)
          callbackError(error)
        })
    })
  }
  /**
   * Formatea un número para que se muestre con decimales solo si los tiene
   * @param {Number} numero
   * @param {Integer} decimales
   * @returns
   */
  static formatoNumeroDecimal = function (numero, decimales = 1) {
    return Math.round(numero) === numero
      ? numero
      : Math.round(numero * 10, decimales) / 10
  }

  static fechaInicioComercializacion(cohorte) {
    if (!cohorte || !cohorte.fecha_inicio) {
      return false
    }
    let fecha_inicio = new Date(Date.parse(cohorte.fecha_inicio))
    let fecha_inicio_comercializacion = new Date(
      fecha_inicio.getFullYear() - 1,
      8,
      1
    )
    return fecha_inicio_comercializacion
  }

  static mergeStatsObjeto = function (newstats, dataprevia, cohortesFechas) {
    let datosNuevos = UAI.obtieneStatsObjeto(newstats, cohortesFechas)
    datosNuevos.allstats.forEach((newstat) => {
      if (
        dataprevia.allstats.find((stat) => {
          return (
            stat.Respuesta === newstat.Respuesta &&
            stat.ProgramaId === newstat.ProgramaId &&
            stat.Fecha === newstat.Fecha
          )
        })
      ) {
        dataprevia.allstats.push(newstat)
      }
    })
    Object.keys(datosNuevos.cohortes).forEach((cid) => {
      if (!dataprevia.cohortes[cid]) {
        dataprevia.cohortes[cid] = datosNuevos.cohortes[cid]
      } else {
        Object.keys(datosNuevos.cohortes[cid])
          .filter((fecha) => {
            return fecha !== "last"
          })
          .forEach((fecha) => {
            if (!dataprevia.cohortes[cid][fecha]) {
              dataprevia.cohortes[cid][fecha] = datosNuevos.cohortes[cid][fecha]
            }
          })
      }
    })
    return dataprevia
  }
  static obtieneStatsObjeto = function (stats, cohortesFechas) {
    if (!cohortesFechas) {
      throw new Error("Parámetros inválidos para calcular objeto stats")
    }
    let dataToStore = { allstats: stats, cohortes: {} }
    let cohortes = stats.reduce((prev, cohortestat) => {
      let fechaestadistica = new Date(Date.parse(cohortestat.Fecha))
      prev[cohortestat.CohorteId] = prev[cohortestat.CohorteId] || {}
      prev[cohortestat.CohorteId][
        UAI.formatoFechaYMD(fechaestadistica)
      ] = cohortestat
      return prev
    }, {})
    Object.keys(cohortes).forEach((cohorteId) => {
      let fechasSolicitadas = cohortesFechas[cohorteId].Fechas.sort((a, b) => {
        return a.localeCompare(b)
      })
      let fechasObtenidas = Object.keys(cohortes[cohorteId]).sort((a, b) => {
        return a.localeCompare(b)
      })
      let j = 0
      for (let i = 0; i < fechasSolicitadas.length; i++) {
        dataToStore.cohortes[cohorteId] = dataToStore.cohortes[cohorteId] || {
          last: null,
        }
        dataToStore.cohortes[cohorteId][fechasSolicitadas[i]] =
          cohortes[cohorteId][fechasObtenidas[j]]
        if (
          !dataToStore.cohortes[cohorteId].last ||
          fechasObtenidas[j].localeCompare(dataToStore.cohortes[cohorteId].last)
        ) {
          dataToStore.cohortes[cohorteId].last =
            cohortes[cohorteId][fechasObtenidas[j]]
        }
        if (cohortes[cohorteId][fechasObtenidas[j + 1]]) {
          j++
        }
      }
    })
    return dataToStore
  }
  static obtieneValoresUFDolar = function (
    usuario,
    callbackSuccess,
    callbackError
  ) {
    let fecha = usuario.fechaReporte.split("T")[0]
    UAI.servicioPostula(
      UAI.basePostula() + "As400/ConsultaValorDolarPorFecha?Fecha=" + fecha,
      "GET",
      null,
      (valorDolar) => {
        UAI.servicioPostula(
          UAI.basePostula() + "As400/ConsultaValorUFPorFecha?Fecha=" + fecha,
          "GET",
          null,
          (valorUF) => {
            callbackSuccess({ uf: valorUF, usd: valorDolar })
          },
          callbackError
        )
      },
      callbackError
    )
  }
  static obtieneAtributosCohortes = function (callbackSuccess, callbackError) {
    UAI.servicioPostula(
      UAI.basePostula() +
      "PostMaestroProducto/ObtenerAtributosMaestroProgramas",
      "GET",
      null,
      callbackSuccess,
      callbackError
    )
  }
  static formatoFechaYMD = function (fecha) {
    var mm = fecha.getMonth() + 1
    if (mm < 10) {
      mm = "0" + mm
    }

    var dd = fecha.getDate()
    if (dd < 10) {
      dd = "0" + dd
    }

    var dateYMD = fecha.getFullYear() + "-" + mm + "-" + dd

    return dateYMD
  }
  static obtieneFacultadPorId = function (id) {
    let _facultad = undefined
    Object.values(UAI.configuracion.facultades).forEach((facultad) => {
      if (facultad.id === id) {
        _facultad = facultad
      }
    })
    return _facultad
  }
  static obtieneFacultadPorNombrecorto = function (nombrecorto) {
    for (var idx in UAI.configuracion.facultades) {
      var facultad = UAI.configuracion.facultades[idx]
      if (facultad.nombrecorto === nombrecorto) {
        return facultad
      }
    }
    return undefined
  }
  static obtieneUnidadVentaPorNombrecorto = function (nombrecorto) {
    for (var idx in UAI.configuracion.unidadesventa) {
      var unidadventa = UAI.configuracion.unidadesventa[idx]
      if (unidadventa.nombrecorto === nombrecorto) {
        return unidadventa
      }
    }
    return undefined
  }

  /**
   * Convierte un string en base 64 a un objeto binario. Se utiliza
   * para mostrar la imagen del usuario informado desde Azure.
   * @param {String} b64Data imagen en formato base64
   * @param {String} contentType tipo de contenido a convertir
   * @param {Integer} sliceSize tamaño del buffer
   * @returns
   */
  static b64toBlob = (b64Data, contentType = "", sliceSize = 512) => {
    const byteCharacters = window.atob(b64Data)
    const byteArrays = []

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize)

      const byteNumbers = new Array(slice.length)
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i)
      }

      const byteArray = new Uint8Array(byteNumbers)
      byteArrays.push(byteArray)
    }

    const blob = new Blob(byteArrays, { type: contentType })
    return blob
  }

  static actualizaAtributo = function (
    usuario,
    cohorteid,
    atributo,
    valor,
    callbackSuccess,
    callbackError
  ) {
    let _atributo = usuario.atributos[atributo]
    if (_atributo.Tipo === "Numerico" && valor === "") {
      valor = null
    }
    UAI.actualizaAtributos(
      usuario.username,
      cohorteid,
      [{ Codigo: atributo, Valor: valor }],
      callbackSuccess,
      callbackError
    )
  }

  static actualizaAtributos = function (
    usuario,
    cohorteid,
    atributosvalores,
    callbackSuccess,
    callbackError
  ) {
    UAI.actualizaAtributosCohortes(
      usuario,
      [{ id: cohorteid }],
      atributosvalores,
      callbackSuccess,
      callbackError
    )
  }

  static obtieneErroresActualizacionAtributosCohortes(resultado) {
    if (
      !resultado ||
      !Array.isArray(resultado) ||
      resultado.reduce((p, c) => c.Solicitudes ? p + 1 : 0, 0) < 1
    ) {
      return {
        Estado: "NOK",
        Mensaje: "Información obtenida de respuesta de Postula es inválida",
        Detalles: [],
      }
    }
    let _message = ""
    let _errores = resultado.reduce((p, c) => {
      p.concat(c.Solicitudes.reduce((prev, current) => {
        let problema = current.Mensaje
        _message += current.Mensaje + "-> "
        let fields = current.CamposPorActualizar.reduce((prev2, current2) => {
          if (current2.Estado === "NOK") {
            prev2[current2.Codigo] = current2.Mensaje
            _message += current2.Codigo + ":" + current2.Mensaje
          }
          return prev2
        }, {})
        if (Object.keys(fields).length > 0) {
          prev.push({ message: problema, fields: fields })
        }
        return prev
      }, []))
      return p
    }, [])
    log.debug(_errores)
    if (_errores.length === 0) {
      return null
    }
    return {
      Estado: "NOK",
      Mensaje: _message,
      Detalles: _errores,
    }
  }

  /**
   * Obtiene la meta actual de postulantes, matriculados o equivalentes
   * para un cohorte en particular
   * @param {Object} cohorte
   * @returns
   */
  static obtieneMetaActualCohorte = function (cohorte) {
    // La meta actual
    let metaActual = undefined
    // Mes actual de acuerdo al arreglo UAI
    let mesActual = UAI.meses[new Date().getMonth()]
    // Si tenemos metas para el cohorte, la obtenemos
    if (
      cohorte.parsed_metas_mensuales &&
      Array.isArray(cohorte.parsed_metas_mensuales.metas) &&
      cohorte.parsed_metas_mensuales.metas.length > 0
    ) {
      metaActual = cohorte.parsed_metas_mensuales.metas.find((m) => {
        return m.Mes === mesActual
      })
    } else {
      metaActual = "N/A"
    }
    return metaActual
  }

  /**
   * Obtiene la meta máxima de postulantes, matriculados o equivalentes
   * para un cohorte en particular
   * @param {Object} cohorte
   * @returns
   */
  static obtieneMetaTotalCohorte = function (cohorte) {
    // La meta actual
    let metaTotal = undefined
    // Si tenemos metas para el cohorte, la obtenemos
    if (
      cohorte.parsed_metas_mensuales &&
      Array.isArray(cohorte.parsed_metas_mensuales.metas) &&
      cohorte.parsed_metas_mensuales.metas.length > 0
    ) {
      metaTotal = cohorte.parsed_metas_mensuales.metas.reduce(
        (prev, current) => {
          if (!prev) {
            return current
          }
          if (
            current["Equivalentes"] > prev["Equivalentes"] ||
            current["Postulantes"] > prev["Postulantes"] ||
            current["Matriculados"] > prev["Matriculados"]
          ) {
            return current
          }
          return prev
        },
        null
      )
    } else {
      metaTotal = "N/A"
    }
    return metaTotal
  }

  /**
   *
   * @param {String} usuario Nombre de usuario activo
   * @param {Array} cohortes Arreglo de cohortes a actualizar
   * @param {Array} atributosvalores Arreglo de cambios por realizar (pares Codigo, Valor)
   * @param {function} callbackSuccess callback de éxito
   * @param {function} callbackError callback de error
   */
  static actualizaAtributosCohortes = function (
    usuario,
    cohortes,
    atributosvalores,
    callbackSuccess,
    callbackError
  ) {
    // curl --header "Content-Type: application/json" --user TD2018:2018TD --data '{"Usuario":"jorge.villalon","Solicitudes":[{"CohorteId":1515,"CamposPorActualizar":[{"Codigo":"meta","Valor":49}]}]}' UAI.basePostula()PostCohorte/ActualizarCohortePorCohorteId
    let solicitudesCohortes = []
    let solicitudesBorradores = []
    cohortes.forEach((cohorte) => {
      if (cohorte.id > 0) {
        solicitudesCohortes.push({
          CohorteId: cohorte.id,
          CamposPorActualizar: atributosvalores,
        })
      } else {
        solicitudesBorradores.push({
          MaestroId: cohorte.id * -1,
          PasoActualId: cohorte.PasoActualId,
          CamposPorActualizar: atributosvalores,
        })
      }
    })
    let requestCohortes = {
      Usuario: usuario,
      Solicitudes: solicitudesCohortes,
    }
    let requestBorradores = {
      Usuario: usuario,
      Solicitudes: solicitudesBorradores,
    }
    log.debug({ cohortes: requestCohortes, borradores: requestBorradores })
    let results = []
    if (solicitudesCohortes.length > 0) {
      UAI.servicioPostula(
        UAI.basePostula() + "PostCohorte/ActualizarCohortePorCohorteId",
        "POST",
        requestCohortes,
        (result) => {
          results.push(result)
          if (solicitudesBorradores.length > 0) {
            UAI.servicioPostula(
              UAI.basePostula() + "PostCohorte/ActualizarCohorte",
              "POST",
              requestBorradores,
              (result2) => {
                results.push(result2)
                callbackSuccess(results)
              },
              callbackError,
              true
            )
          } else {
            callbackSuccess(results)
          }
        },
        callbackError,
        true
      )
    } else if (solicitudesBorradores.length > 0) {
      UAI.servicioPostula(
        UAI.basePostula() + "PostCohorte/ActualizarCohorte",
        "POST",
        requestBorradores,
        (result) => {
          results.push(result)
          callbackSuccess(results)
        },
        callbackError,
        true
      )
    } else {
      callbackError()
    }
  }

  /**
   * Inserta una nueva cohorte en estado borrador
   * @param {String} usuario Usuario que ingresa el borrador
   * @param {Integer} programaid ID del programa al que pertenece la cohorte
   * @param {Integer} modalidadid ID de modalidad
   * @param {function} callbackSuccess callback de éxito
   * @param {function} callbackError callback de error
   */
  static insertarBorradorCohorte = function (
    usuario,
    programaid,
    modalidadid,
    nombre,
    callbackSuccess,
    callbackError
  ) {
    let request = {
      Usuario: usuario,
      ProgramaId: programaid,
      ModalidadId: modalidadid,
    }
    log.debug(request)
    UAI.servicioPostula(
      UAI.basePostula() + "PostCohorte/IngresarCohorteBorrador",
      "POST",
      request,
      (result) => {
        if (result && result.MaestroId) {
          UAI.actualizaAtributosCohortes(usuario, [{ id: -1 * result.MaestroId }], [{ "Codigo": "fecha_inicio", "Valor": UAI.formatoFechaYMD(new Date()) }, { "Codigo": "nombre", "Valor": nombre }], (r) => {
            callbackSuccess(result)
          }, (e) => {
            callbackError
          })

        } else {
          callbackError
        }
      },
      callbackError,
      true
    )
  }

  /**
   * Cambia el estado de una postulación por su ID. No todos
   * los estados están permitidos, por ejemplo Aceptado y Matriculado
   * no lo están, mientras que Aceptado académico y Por Entrevistar si.
   * @param {Integer} postulacionid ID de la postulación
   * @param {String} nuevoestado Nombre del nuevo estado
   * @param {String} comentarios Comentarios
   * @param {function} callbackSuccess callback de éxito
   * @param {function} callbackError callback de error
   */
  static actualizaEstadoPostulacion = function (
    postulacionid,
    nuevoestado,
    comentarios,
    callbackSuccess,
    callbackError
  ) {
    let request = [
      {
        PostulacionId: postulacionid,
        Estado: nuevoestado,
        Comentario: comentarios,
      },
    ]
    UAI.servicioPostula(
      UAI.basePostula() +
      "PostPostulacion/ActualizarEstadoPostulacionPendienteEntrevista",
      "POST",
      request,
      callbackSuccess,
      callbackError
    )
  }
  /**
   * Obtiene una postulación a partir de su ID
   * @param postulacionid El ID de la postulación
   * @param callbackSuccess Función que se invocará si todo sale bien
   * @param callbackError Función que se invocará si hay algún error
   */
  static obtienePostulacionPorId = function (
    postulacionid,
    callbackSuccess,
    callbackError
  ) {
    let request = {
      FiltroPostulanteNombre: "",
      FiltroNumeroDocumento: "",
      pagina: 1,
      MaximoPorPagina: 50,
      Usuario: "jorge.villalon",
      TamanoPagina: 50,
      NumeroPagina: 1,
      Orden: "PostulacionId DESC",
      PostulacionId: postulacionid,
    }
    UAI.servicioPostula(
      UAI.basePostula() +
      "PostPostulacion/ObtenerPostulacionPorFiltrosPorPostulacionId",
      "POST",
      request,
      callbackSuccess,
      callbackError
    )
  }

  /**
   * Obtiene todos los adjuntos de una postulación, con el id de la misma. Cada documento viene en formato base64
   * @param postulacionid El ID de la postulación
   * @param callbackSuccess Función que se invocará si todo sale bien
   * @param callbackError Función que se invocará si hay algún error
   */
  static obtieneDocumentosPostulacion = function (
    postulacionid,
    callbackSuccess,
    callbackError
  ) {
    let tipodocumentos = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]
    let request = []
    for (let id in tipodocumentos) {
      request.push({
        PostulacionId: postulacionid,
        TipoDocumentoId: tipodocumentos[id],
      })
    }
    UAI.servicioPostula(
      UAI.basePostula() + "PostPostulacion/ObtenerDocumentoPostulacion",
      "POST",
      request,
      callbackSuccess,
      callbackError
    )
  }
  /**
   * Obtiene todos los adjuntos de un conjunto de postulaciones.
   * @param cohorte El cohorte del cual provienen las postulaciones
   * @param postulacionid El ID de la postulación
   * @param callbackSuccess Función que se invocará si todo sale bien
   * @param callbackError Función que se invocará si hay algún error
   */
  static obtieneDocumentosPostulacionesCohorte = function (
    cohorte,
    postulaciones,
    descargar,
    callbackSuccess,
    callbackError
  ) {
    let tipodocumentos = cohorte.documentos_requeridos.split(",")
    let request = []
    postulaciones.forEach((p) => {
      tipodocumentos.forEach((tipo) => {
        request.push({
          PostulacionId: p.PostulacionId,
          TipoDocumentoId: tipo,
          ObtenerDocumento: descargar !== undefined && descargar,
        })
      })
    })

    UAI.servicioPostula(
      UAI.basePostula() + "PostPostulacion/ObtenerDocumentoPostulacion",
      "POST",
      request,
      callbackSuccess,
      callbackError
    )
  }
  /**
   * Obtiene un documento de una postulación, con el id de la misma y el tipo de id del documento. Cada documento viene en formato base64
   * @param postulacionid El ID de la postulación
   * @param tipodocumentoid El ID del tipo de documento a obtener
   * @param callbackSuccess Función que se invocará si todo sale bien
   * @param callbackError Función que se invocará si hay algún error
   */
  static obtieneDocumentoPostulacion = function (
    postulacionid,
    tiposdocumentos,
    callbackSuccess,
    callbackError
  ) {
    let request = []
    tiposdocumentos.forEach((tipodocumentoid) => {
      request.push({
        PostulacionId: postulacionid,
        TipoDocumentoId: tipodocumentoid,
      })
    })
    UAI.servicioPostula(
      UAI.basePostula() + "PostPostulacion/ObtenerDocumentoPostulacion",
      "POST",
      request,
      callbackSuccess,
      callbackError
    )
  }

  static clonaUsuarioNuevosAtributos = function (usuario, cambios) {
    if (!cambios.Solicitudes || cambios.Solicitudes.length === 0) {
      log.warn("Datos inválidos devueltos")
      return undefined
    }
    log.info("Cambiando valores en objeto")
    let updateBenchmark = []
    let _usuario = cloneDeep(usuario)
    cambios.Solicitudes.forEach((solicitud) => {
      let _cohorteId = solicitud.CohorteId
      solicitud.CamposPorActualizar.forEach((campo) => {
        if (campo.Estado === "OK") {
          log.info("Actualizando")
          log.debug(campo)
          log.debug(_usuario.cohortes[_cohorteId])
          let atributo = _usuario.atributos[campo.Codigo]
          if (atributo === undefined) {
            throw new Error("Atributo inválido a actualizar " + campo.Codigo)
          }
          if (atributo.Tipo === "Numerico") {
            campo.Valor = parseInt(campo.Valor)
          }
          if (campo.Codigo === "programaid_anterior") {
            updateBenchmark.push({
              benchmarkid: campo.Valor,
              cohorteid: _cohorteId,
            })
          }
          if (_usuario.cohortes[_cohorteId]) {
            _usuario.cohortes[_cohorteId][campo.Codigo] = campo.Valor
            log.debug(_usuario.cohortes[_cohorteId][campo.Codigo])
          }
        } else {
          log.error("Error grabando datos!")
          log.error(solicitud)
        }
      })
    })
    _usuario.hash = Math.random()
    if (updateBenchmark.length > 0) {
      updateBenchmark.forEach((upd) => {
        _usuario.cohortes[upd.benchmarkid].esBenchmark = true
        _usuario.cohortes[upd.benchmarkid].esBenchmarkId = upd.cohorteid
      })
    }
    _usuario.stats = undefined
    return _usuario
  }
}

export default UAI
